// translations.js
// Add new keys here in both languages.
// Montenegrin (me) uses Latin script.

const translations = {
  en: {
    // Top bar
    searchPlaceholder: "Search SAMST Network",

    // Category buttons
    catAnomalies: "Anomalies",
    catTiltSensors: "Tilt sensors",
    catMotionSensors: "Motion sensors",
    catTempSensors: "Temp sensors",
    catWeatherStations: "Weather stations",
    catPowerMeters: "Power meters",
    // Short forms for the mobile chip row, where the full labels overflow the
    // viewport. The icon already carries the "sensor" meaning.
    catTiltShort: "Tilt",
    catMotionShort: "Motion",
    catTempShort: "Temp",
    catWeatherShort: "Weather",
    catPowerShort: "Power",

    // Anomalies panel
    detectedAnomalies: "Detected Anomalies",
    noAnomalies: "No anomalies detected",
    allSystemsNormal: "All systems normal",
    anomaliesError: "Failed to load anomalies.",
    rawPayload: "Raw payload",
    confidence: "confidence",
    weatherNotChecked: "Weather not checked for this detection",
    showMotionEvents: "Show motion events",
    loadMore: "Load more",
    moreCount: "+{n} more",
    clickToExpand: "click to view all",
    today: "Today",
    yesterday: "Yesterday",
    unknownDate: "Unknown date",
    loading: "Loading...",

    // Repeated-anomaly roll-up (#211). Shown only in the badge tooltip, so
    // these are full sentences rather than labels.
    rollupTitle: "{count} readings folded into one entry",
    rollupVariants: "{count} different messages",
    rollupTruncated: "More continue past this page",

    // Worklist rows (#208). The type labels are what an operator calls the
    // finding, not what the detector calls its column: "motion", not "pir".
    anomalyTypeTilt: "tilt",
    anomalyTypeMl: "tilt (ML)",
    anomalyTypeTemperature: "temp",
    anomalyTypeMotion: "motion",
    anomalyTypeMounting: "mounting",
    anomalyTypeFlood: "flood",
    towerEntries: "{n} entries at this tower",
    rollupFolded: "Readings folded into this row",
    // The cap on the folded sample, stated rather than left to read as the
    // whole run.
    rollupShowing: "newest {shown} of {count}",

    // Node info panel
    nodeInfo: "Node Info",
    sensorData: "Sensor Data",
    loadingSensorData: "Loading sensor data...",
    failedToLoadSensor: "Failed to load sensor data.",
    readings: "Readings",
    // Placeholder-named channels are counted rather than listed — see
    // isPlaceholderChannel in App.js.
    hiddenChannels: "{n} unidentified channels not shown",
    close: "Close",
    backToMap: "← Back to Map",

    // Sensor fields
    fieldSensor: "Sensor",
    fieldTimestamp: "Timestamp",
    fieldType: "Type",

    // Health status
    statusOk: "Online",
    statusWarning: "Battery low",
    statusOffline: "Offline",
    statusAlert: "Alert",
    statusUnknown: "Status unknown",
    lastSeen: "Last seen",
    // Elapsed time is assembled by formatLastSeen in relativeTime.js, which
    // picks one of these by plural category. There is deliberately no bare
    // "ago" suffix key left to concatenate a number onto — see the me block.
    lastSeenJustNow: "just now",
    lastSeenMinutesOne: "{n} minute ago",
    lastSeenMinutesFew: "{n} minutes ago",
    lastSeenMinutesOther: "{n} minutes ago",
    lastSeenHoursOne: "{n} hour ago",
    lastSeenHoursFew: "{n} hours ago",
    lastSeenHoursOther: "{n} hours ago",
    lastSeenDaysOne: "{n} day ago",
    lastSeenDaysFew: "{n} days ago",
    lastSeenDaysOther: "{n} days ago",
    battery: "Battery",

    // Health / config availability
    healthDataUnavailable: "Health data unavailable — sensor status is unknown.",
    healthRetrying: "Retrying every minute.",
    nodesLoadError: "Could not load nodes — the map may be incomplete.",
    nodesLoadErrorHint: "Refresh the page to try again.",
    nodesSaveError: "Change was not saved.",
    nodesSaveErrorHint: "It is shown here only and will be gone after a refresh.",
    // Deleting a node
    delete: "Delete",
    deleteNode: "Delete node",
    deleteNodeConfirm: "Delete this node?",
    deleteNodeWarning: "This removes the node and its {n} assigned sensor(s) from the map. It cannot be undone.",
    noHealthDataForDevice: "No health data for this device.",
    // Named per sensor: on a node with several sensors, "unknown" has to say
    // which one it cannot see.
    noHealthDataForSensors: "No health data for: {list}",
    sensorHealthError: "Could not load sensor health.",
    configDegradedBanner: "Configuration service degraded.",
    configDegradedHint: "Health thresholds shown may be out of date.",
    configDegradedBadge: "Degraded configuration",
    configLoadError: "Settings could not be loaded",
    configDegradedNotice: "These are fallback values, not the saved settings. Editing is disabled until the configuration service recovers.",
    weatherStatusUnavailable: "Weather status unknown — no source could be reached.",
    weatherStatusPartial: "Weather check incomplete — a source could not be reached.",
    // Weather banner detail (#92). Event, severity and certainty come straight
    // from Meteoalarm and are shown untranslated — see WeatherAlertDetail.jsx.
    weatherAlertCount: "{n} active alerts",
    weatherAlertCountOne: "1 active alert",
    weatherAlertStartUnknown: "start unknown",
    weatherAlertNoExpiry: "no expiry given",
    weatherAlertRegions: "Regions: {list}",
    weatherAlertRegionsUnrecorded: "{n} regional warnings — regions not recorded",
    weatherAlertShowDetail: "Show alert detail",
    weatherAlertHideDetail: "Hide alert detail",

    // Fire detections (#93) — NASA FIRMS satellite thermal anomalies.
    //
    // Wording rule for every string in this block: a FIRMS record is a thermal
    // ANOMALY, never a confirmed fire, and an empty result is "nothing was
    // flagged in what we checked", never "there is no fire". Industrial heat and
    // sun glint both produce detections, and a satellite passes overhead a few
    // times a day rather than watching continuously. Do not soften these into
    // "fire detected" or "no fires" when editing.
    catFires: "Fire detections",
    catFiresShort: "Fires",
    fireThermalAnomaly: "Thermal anomaly",
    fireNotConfirmed: "Satellite detection — not a confirmed fire",
    fireConfidenceLabel: "Confidence",
    fireConfidence_low: "low",
    fireConfidence_nominal: "nominal",
    fireConfidence_high: "high",
    fireConfidencePct: "{n}%",
    fireConfidenceUnknown: "not reported",
    fireAcquired: "Acquired",
    fireAgeMinutes: "{n} min ago",
    fireAgeHours: "{n} h ago",
    fireAgeDays: "{n} d ago",
    fireAgeUnknown: "time unknown",
    fireDistanceToNode: "{km} km from {node}",
    fireWithinRadius: "within {km} km",
    fireDistanceUnknown: "Distance to nodes unknown — the node list could not be read.",
    fireNoNodes: "No nodes on the map to measure against.",
    fireRadiativePower: "Radiative power",
    fireCellCentre: "Cell centre",
    fireCellSize: "~{m} m detection cell",
    fireBannerNear: "Thermal anomaly {km} km from {node}, {age} — {confidence} confidence. Satellite detection, not a confirmed fire.",
    fireBannerSomeFar: "{n} thermal anomalies in the last {hours} h, none within {km} km of a node. Detections, not confirmed fires.",
    fireBannerNone: "No thermal anomalies flagged in the last {hours} h. Satellites revisit a few times a day — this is not proof there is no fire.",
    fireBannerStale: "Fire detection status unknown — last check was {n} min ago.",
    fireBannerPartial: "Fire check incomplete — {failed} of {total} satellite sources could not be reached. {n} thermal anomalies from those that answered.",
    fireBannerUnavailable: "Fire detection status unknown — FIRMS could not be reached.",
    fireBannerUnavailableReason: "Fire detection status unknown — {reason}.",
    // Appended to whichever banner is quoting a count, when detections reach
    // the wall of the polled box (#100). The count is real; what it is not is a
    // total, because the satellite was only asked about a rectangle and the
    // cluster carries on past its edge. Said plainly, in the same observational
    // register as the rest of this block.
    fireBannerClipped: "Detections reach the edge of the monitored area, so this is a count inside it, not a total.",
    // Appended to the fires help whenever the chip is showing "n+" (#171). Not
    // hover text any more — it was a `title=` attribute, which a phone cannot
    // open, on the one number that most needs the qualification.
    fireCountBounded: "The real number is higher. Hot spots were found right at the edge of the area we ask about, so the count stops there. Whatever lies outside was never checked.",

    // Sentinel-2 vegetation indicator (#103).
    //
    // Every measured line carries the acquisition date, because an NDVI figure
    // without one is read as today's and the scene behind it can be weeks old.
    //
    // No line names a cause. NDVI falls for fire, felling, drought, harvest and
    // grazing alike and cannot distinguish them, so the wording stops at what
    // was measured — "cause not identified" is the honest end of the sentence,
    // and "possible fire" would not be.
    // The three absolute-class lines below are NOT rendered — App.js shows a
    // sudden drop and nothing else. Their thresholds know nothing about the
    // month, and Mediterranean vegetation dries every summer, so an absolute
    // badge would read amber for a third of the year with nothing having
    // happened. Kept, translated, ready: once there are two summers of readings
    // the comparison becomes seasonal rather than absolute and these earn their
    // place. See the note above `ndviBanner` in App.js, and #103.
    ndviBannerNormal: "Vegetation normal — {date}, NDVI {value}.",
    ndviBannerModerateStress: "Vegetation stress — {date}, NDVI {value}. Area average; cause not identified.",
    ndviBannerHighStress: "High vegetation stress — {date}, NDVI {value}. Area average; cause not identified.",
    ndviBannerSuddenDrop: "NDVI fell {drop} in {days} days — {date}, now {value}. Cause not identified: fire, felling and drought all look like this.",
    ndviBannerNoScene: "Vegetation not observed — no cloud-free Sentinel-2 scene. This says nothing about the corridor.",
    ndviBannerNoSceneReason: "Vegetation not observed — {reason}. This says nothing about the corridor.",
    ndviBannerStale: "Vegetation status unknown — last check was {n} days ago.",
    ndviBannerUnavailable: "Vegetation status unknown — Copernicus could not be reached.",
    ndviBannerUnavailableReason: "Vegetation status unknown — {reason}.",
    // Appended to a measured line when cloud left only part of the box visible.
    // A mean over 40% of the corridor is a different claim from one over all of it.
    ndviPartialView: " Only {pct}% of the area was visible.",
    ndviDateUnknown: "date unknown",

    // Sentinel-1 ground stability, per pylon (#118).
    //
    // No line grades the reading. Coherence is stated with both dates of the pair
    // it was measured across, because it is a comparison between two passes and a
    // single date would present an interval as an instant.
    //
    // "not displacement" is in the drop line on purpose: that is the reading most
    // likely to be acted on, and radar deformation monitoring is exactly what a
    // reader might assume it is.
    groundStability: "Ground stability (radar)",
    groundStabilityLoading: "Checking…",
    groundStabilityError: "Could not load the radar reading.",
    groundStabilityUnavailable: "No radar reading for this node yet.",
    groundStabilityNotMeasured: "Not measured on the last pass — the ground around this node decorrelated. This says nothing about the tower.",
    groundStabilityFirst: "Coherence {value} · {window}. First reading — nothing to compare against yet.",
    groundStabilityOk: "Coherence {value} · {window}. In line with this node's recent readings.",
    groundStabilityDrop: "Coherence {value} · {window} — down {drop} from this node's usual {median}. Something changed here; not a displacement measurement, and the cause is not identified.",
    // #194. A crew was sent to node-002 on this reading and found nothing: both
    // radar passes fell on red rain days and the ground there is bare rock, so
    // the pixel was reading standing water. The next clear pair recovered fully.
    // The line names the mechanism because "ignore it, it rained" is the wrong
    // lesson — over the same pair another node's coherence went up.
    groundStabilitySuspect: "Coherence {value} · {window} — down {drop} from this node's usual {median}, but severe rain was recorded over this pair ({weather}). Standing water scatters radar away from the satellite and the ground recovers once it dries, so this pair cannot tell a ground change from a wet surface. The next pair with no such alert settles it.",
    groundStabilityWeather: " Weather over this pair: {weather}.",
    groundStabilityWeatherUnnamed: "alert details not recorded",
    groundStabilityStale: " Last checked {n} days ago.",
    groundStabilityDatesUnknown: "dates unknown",

    // Line identity on the node panel (#209). Read from power_lines.geojson,
    // which is already loaded to draw the corridor.
    //
    // lineSource is shown on every node without exception. The match is the
    // nearest mapped line, not a record of what the tower is wired to, and the
    // panel has to say so: #136 ruled that nothing about the corridor may be
    // inferred from this file, and a voltage printed beside a tower is acted on.
    lineIdentity: "Line",
    lineCircuit: "1 circuit",
    lineCircuits: "{n} circuits",
    lineCable: "1 cable",
    lineCables: "{n} cables",
    lineOperator: "operator {name}",
    lineSource: "Nearest mapped line, {distance} away.",
    lineNotOnCorridor: "This node is not on the monitored corridor, so this line is not necessarily its circuit.",

    // The always-on line. States a measurement and a date and stops there — no
    // "normal", no "stress", nothing the reader could take as a verdict, because
    // the number cannot support one until there is a seasonal baseline to compare
    // it against. "Area average" is in the line rather than a footnote: it is the
    // single fact that stops 0.38 being read as a statement about any one span.
    ndviBannerReading: "Corridor NDVI {value} · {date} · area average",
    // Appended when the weekly run has stopped landing. A number with no note
    // would read as current.
    ndviBannerReadingStale: " · not updated in {n} d",

    // Per-segment corridor NDVI, the GeoServer WMS layer (#146).
    //
    // The banner above reports ONE number over a ~180 km² box; these strings
    // describe spans of a few hundred metres between consecutive towers. Hence
    // "segment" everywhere and "area average" nowhere.
    //
    // Three rules hold across every string here:
    //   * An absent measurement is described by its CAUSE and never by a number.
    //     Cloud, outside-scene and a failed check are three different facts.
    //   * A value never appears without its age.
    //   * Nothing grades anything. No "normal", no "stress", no colour word —
    //     the baseline difference is published as a signed number with the depth
    //     of history behind it, and the reader draws the conclusion.
    catSegmentNdvi: "Corridor segments (NDVI)",
    catSegmentNdviShort: "Segments",
    // `segmentChipTitle` used to be here. It was a `title=` attribute on the
    // chip — hover-only, so absent on a phone — and its sentence is now the
    // opening of `helpSegmentNdvi`, which every device can open (#171).
    segmentLayerTitle: "Corridor segments — NDVI",
    // The caveat that used to live here as `segmentLayerCaveat` is now
    // `helpNdviClaim`, in the online-help block at the foot of this file. It
    // was one of two copies of one claim — the other was `ogcCaveat` — kept in
    // step by a comment and nothing else (#171). The layer still renders it,
    // unchanged in force and never dropped for space: #135 exists because a
    // comment claimed a reading described a tower when it described the ground
    // around it, and this layer draws that same claim on a map.
    segmentLabel: "Segment {n}",
    segmentAgeToday: "measured today",
    segmentAgeDays: "measured {n} d ago",
    segmentCarriedForward: "no usable acquisition since",
    segmentPartialView: "Only {pct}% of the segment was visible.",
    segmentVsBaseline: "{delta} vs this segment's baseline for the month ({n} obs.)",
    segmentNoBaseline: "No baseline for this segment yet.",
    // Four causes, four strings. Collapsing them into one "no data" would undo
    // the distinction the database schema, the SLD and this panel all carry.
    segmentNoValueCloud: "no value — clouded",
    segmentNoValueOutside: "no value — outside every scene",
    segmentNoValueUnavailable: "no value — the check failed",
    segmentNoValueNever: "never measured",
    segmentNoneDefined: "No corridor segments are defined yet.",
    segmentDetailsUnavailable: "Segment values could not be read.",

    // Online help (#172). One string, used as both the tooltip and the
    // aria-label on the help control: the control is an icon, so the tooltip IS
    // its label and a screen reader must get the same sentence a sighted user
    // gets on hover. It names the destination and says what will happen, because
    // a control that silently spawns a window on another monitor is worse than
    // one that warns you it will.
    helpManual: "Operator's manual — opens in a separate window",

    // Sidebar
    sensorList: "Sensor List",
    settings: "Settings",
    about: "About",
    healthThresholds: "Health monitoring thresholds",
    offlineAfter: "Offline after",
    batteryWarningBelow: "Battery warning below",
    failedToSave: "Failed to save. Try again.",
    aboutDescription: "Smart Asset Monitoring System for Transmission infrastructure.",
    poweredBy: "Powered by LoRaWAN sensors, AWS, and React.",

    // Corridor report (#143) — the sidebar control for the /report endpoints
    // #131 shipped. The two progress labels name stages the server actually
    // passes through rather than a timer's ticks, so they have to stay accurate:
    // "assembling" is the wait on the server, which is nearly all of it, and
    // "downloading" is the transfer after the headers arrive.
    corridorReport: "Corridor report",
    reportPeriodPrompt: "Choose a period, then press Apply.",
    reportPeriodSelected: "Period: {start} → {end}",
    reportDownloadPdf: "PDF",
    reportDownloadCsv: "CSV",
    reportAssembling: "Assembling report…",
    reportDownloading: "Downloading…",
    reportDone: "Done",
    // Says "produce", not "load": what failed was the assembly of a document,
    // and the operator must not read it as a period that turned out to be empty.
    reportFailed: "Could not produce the report. Try again.",
    reportEmptyPeriodNote: "Covers the whole corridor. A period with no readings still produces a report — devices that reported nothing are listed, not omitted.",

    // Connect your GIS (#164) — the sidebar panel naming the OGC endpoint and
    // the two layers #146 published. The panel displays constants and fetches
    // nothing, so every string here is a claim that has to be true on its own.
    //
    // Two rules hold across these strings:
    //   * Nothing here claims a capability that has not been exercised. QGIS and
    //     ArcGIS are named because both consume OGC WMS/WFS directly — a
    //     statement about the standard, not a test report.
    //   * The NDVI caveat is NOT restated here. It used to be: `ogcCaveat` and
    //     `segmentLayerCaveat` were two renderings of one claim, and the comment
    //     that had to keep them aligned was the only thing keeping them aligned.
    //     The panel now renders `helpNdviClaim` — the claim itself, one string,
    //     shared with the map layer and with the help topics — followed by
    //     `ogcCaveatGis`, which is the single sentence that was only ever true
    //     of an export into someone else's system (#171).
    ogcTitle: "Connect your GIS",
    ogcIntro: "The corridor is published as standard OGC layers. Add them to the GIS you already run and they update by themselves — your software reads live from this server.",
    ogcEndpointLabel: "WMS / WFS endpoint",
    ogcCopy: "Copy",
    ogcCopied: "Copied",
    ogcCopyManual: "The URL is selected — press Ctrl+C to copy it.",
    ogcEndpointHint: "One address for both services; the client adds WMS or WFS itself. A WFS-only client can use /geoserver/wfs.",
    // The endpoint above is a dispatcher and answers "No service: ( OWS )" when
    // opened bare in a browser. That is correct OGC behaviour, and it is also the
    // first thing anyone does with an address — so say it before they try it,
    // rather than letting a working endpoint look broken in front of a customer.
    ogcVerify: "Check it in this browser",
    ogcVerifyHint: "The address above is for a GIS client, not a browser — opened on its own it returns an error, which is correct. This link adds what a client would send, so the server answers with its own description of both layers.",
    ogcLayersLabel: "Layers",
    ogcLayerLatest: "Current state — one row per segment, with the age of the value and the reason when there is none.",
    ogcLayerSeries: "Full history, about 11 years of Sentinel-2 — carries a WMS TIME dimension, so QGIS's temporal controller gives you a date control with no setup.",
    ogcClients: "QGIS and ArcGIS both add an OGC endpoint directly: no plugin, no project file, no export to keep in step.",
    // Rendered immediately after helpNdviClaim, which carries the claim itself.
    ogcCaveatGis: "When this becomes a layer in your own GIS, none of that explanation travels with it.",

    // Dialog
    assignSensor: "Assign Sensor to Pin",
    selectSensor: "Select Sensor",
    // Shown in the dialog itself, beside the selection that caused it (#199).
    // Names the node, because the operator picked it from a list of several and
    // "already assigned" alone does not say to which.
    sensorAlreadyOnNode: "This sensor is already assigned to {node}.",
    areaName: "Area name (optional)",
    areaPlaceholder: "e.g. Podgorica North",
    cancel: "Cancel",
    addPin: "Add Pin",

    // Register end node
    registerDevicePrompt: "Device not listed? Register it",
    registerDevicePromptEmpty: "No devices yet — register one",
    registerDeviceTitle: "Register end node",
    registerDeviceHint: "Tells SAMST the device exists. It does not provision anything in TTN — the device must already be transmitting.",
    registerDeviceId: "Device ID (as in TTN)",
    registerDeviceType: "Sensor type",
    registerDeviceLabel: "Label (optional)",
    register: "Register",
    registering: "Registering…",
    noDataYetShort: "no data yet",
    noDataYetTitle: "No readings yet",
    noDataYetBody: "This device is registered but has not reported. Readings appear here once its first uplink is decoded.",
    registeredOn: "Registered",

    // Drift chart
    driftChartLoading: "Loading data…",
    driftChartNoData: "No readings in this time range.",
    driftChartError: "Failed to load",
    range24h: "24 h",
    range7d: "7 days",
    range30d: "30 days",
    // A convenience window, not a cap. #184 removed the six-month ceiling and
    // #231 added this preset; the two must not be read as the same thing.
    range6m: "6 months",
    rangeCustom: "Custom",
    customFrom: "From",
    customTo: "To",
    // Sets the From field to the 2026-01-01 data floor in one click (#231).
    // Deliberately not "this year": the two coincide now and diverge on
    // 2027-01-01, when a calendar-year shortcut would start hiding 2026.
    fromStart: "Since the start",
    fromStartTitle: "Set the start date to 1 January 2026, the earliest data the system holds",
    apply: "Apply",
    baselineX: "Baseline X",
    baselineY: "Baseline Y",
    baselineZ: "Baseline Z",
    peakDrift: "Peak drift",
    currentDrift: "Current drift",
    xAxis: "X axis",
    yAxis: "Y axis",
    zAxis: "Z axis",
    alertThreshold: "Threshold",
    baselineWarning: "Baseline computed from {n} readings (fewer than 24). Will stabilise as more data arrives.",
    spikeNoticeOne: "1 implausible reading filtered from chart — preserved in raw data",
    spikeNotice: "{n} implausible readings filtered from chart — preserved in raw data",
    // Ingestion gaps (#167). Worded to separate "we stopped listening" from
    // "the tower was still" — the chart draws the same flat line for both, and
    // before this nothing distinguished them.
    ingestGapLost: "⚠ Missing data: ingestion stopped for {d} in this range, and {u} of it is past the network's retention and cannot be recovered. The line is drawn straight across it.",
    ingestGapUnknown: "⚠ Ingestion stopped for {d} in this range and recovery could not be checked. Readings in the shaded span may be missing.",
    ingestGapRecovered: "Ingestion stopped for {d} in this range. Every reading was recovered.",
    ingestGapLegend: "Ingestion outage",
    updated: "Updated",
    // Drift report export (#141). The PDF circulates without the session that
    // produced it, so its header has to state what each number is conditional on.
    exportTitle: "Drift report",
    exportDeviceId: "Device ID",
    exportedAt: "Exported",
    exportRangeLabel: "Range",
    exportBaselineLine: "Baseline — X: {x}°  Y: {y}°  Z: {z}°",
    exportAlertPoints: "Points with a threshold crossing: {n} of {total}",
    exportPeakDrift: "Peak drift: {v}°",
    exportThreshold: "Threshold: ±{v}°",
    exportSpikeOne: "1 reading filtered as a spike — a transient excursion, excluded from drift.",
    exportSpikeMany: "{n} readings filtered as spikes — transient excursions, excluded from drift.",
    exportSpikeCaveat: "The peak drift above is from the filtered series.",
    exportSpikeNone: "No readings were filtered as spikes in this range — the peak drift above is the full series.",
    // SenseCAP S2120 weather station panel (#77)
    wsAirTemperature: "Air temperature",
    wsHumidity: "Humidity",
    wsPressure: "Pressure",
    wsWindSpeed: "Wind speed",
    wsWindDirection: "Wind direction",
    wsIlluminance: "Illuminance",
    wsUvIndex: "UV index",
    // Rain and gust (#243)
    wsWindGust: "Peak gust",
    wsRain: "Rain",
    wsRainRate: "Rain rate",
    wsRain24h: "Rain, last 24 h",
    wsRainRateNote: "The rain rate is the station's own figure for the current reading. It jumps on a single tip of the rain gauge, so read it together with the 24 h total. A total marked ≥ leaves out an interval the station could not count.",
    wsBattery: "Battery",
    wsJustNow: "just now",
    wsAgo: "ago",
    wsUnverifiedNote: "The station also reports channels whose scaling is not yet confirmed. They are stored but not shown as measurements:",
    // Weather station history charts (#85)
    wsHistoryTitle: "Measurement history",
    wsNoParameterSelected: "Select a parameter to plot",
    wsWindDirectionNote: "Plotted as points, not a line — a bearing crossing north would otherwise draw a full-height jump between 359° and 1°.",
    wsChartRainNote: "Rain is the amount that fell in each plotted interval, from the station's rain gauge. The rain rate is not plotted because it jumps on a single tip of the gauge; it is shown as a current value in the panel above.",
    // Acrel ADW300 electrical meter panel and charts (#99)
    pmGroupLine: "Line",
    pmGroupPower: "Power",
    pmGroupEnergy: "Energy totals",
    pmVoltageA: "Voltage L1",
    pmVoltageB: "Voltage L2",
    pmVoltageC: "Voltage L3",
    pmVoltageAB: "Voltage L1-L2",
    pmVoltageBC: "Voltage L2-L3",
    pmVoltageCA: "Voltage L3-L1",
    pmCurrentA: "Current L1",
    pmCurrentB: "Current L2",
    pmCurrentC: "Current L3",
    pmActivePower: "Active power",
    pmActivePowerA: "Active power L1",
    pmActivePowerB: "Active power L2",
    pmActivePowerC: "Active power L3",
    pmReactivePower: "Reactive power",
    pmReactivePowerA: "Reactive power L1",
    pmApparentPower: "Apparent power",
    pmApparentPowerA: "Apparent power L1",
    pmPowerFactor: "Power factor",
    pmPowerFactorA: "Power factor L1",
    pmEnergyTotal: "Total energy",
    pmEnergyForward: "Energy imported",
    pmEnergyReverse: "Energy exported",
    pmReactiveEnergyForward: "Reactive energy imported",
    pmReactiveEnergyReverse: "Reactive energy exported",
    pmPtRatio: "PT ratio",
    pmCtRatio: "CT ratio",
    pmSinglePhase: "Single phase",
    pmPhaseCount: "{n} phases",
    pmCumulative: "cumulative",
    pmHistoryTitle: "Electrical history",
    pmNoParameterSelected: "Select a parameter to plot",
    pmNegativePowerNote: "Active power is negative. That means power is flowing in the direction the meter calls reverse — either the line is exporting, or the CT clamp is fitted the wrong way round. Which one has not been confirmed at the meter yet, so the sign is reported exactly as measured.",
    pmCtRatioOneNote: "a CT ratio of 1 means no clamp scaling is applied; confirm it matches the fitted clamp",
    pmUnverifiedNote: "The meter also reports channels whose meaning or scaling is not yet confirmed. They are stored but not shown as measurements:",
    pmChartUnverifiedNote: "Only channels whose scaling has been confirmed against the meter's own readings are plotted. An unconnected phase is omitted rather than drawn at zero, and a gap in the line is a missing uplink, not a reading of zero.",
    normalStatus: "Normal",
    alertStatus: "alerts",
    thresholdExceeded: "Threshold exceeded",
    enterBothDates: "Please select both dates.",
    // The corridor report's limit only, since #184. The charts no longer have a
    // maximum span, so a chart never renders this.
    maxRange: "Maximum range is 6 months.",
    endAfterStart: "End date must be after start date.",
    customRangePrompt: "Choose a start and end date, then press Apply.",

    // The chart range floor (#184). Names the date rather than saying "invalid",
    // so an operator who picked November 2025 learns what to pick instead.
    minRange: "Earliest available date is 1 January 2026. The system holds no measurements before then.",
    // A device that did not yet exist must not look like a device that went
    // quiet. Four of the pilot devices came online months after the floor.
    preDataPartial: "This device has no readings before {date}. The blank stretch before that date is when it was not yet reporting, not an outage.",
    preDataNone: "This device has no readings before {date}, so this range contains nothing to plot. It is not an outage.",

    // Chart aggregation disclosure (#108). A long range is decimated to ~300
    // points; before this the fact was in the API response and never said.
    aggPlotRaw: "Raw",
    aggPlotAggregated: "Aggregated",
    aggNotice: "Aggregated: each point spans ~{span} and averages {n} readings. The shaded band is the lowest and highest reading actually taken in that interval; the line is their mean.",
    aggNoticeNoSpan: "Aggregated: each point averages {n} readings. The shaded band is the lowest and highest reading actually taken in that interval; the line is their mean.",
    aggNoticeLatest: "The newest point is the latest single reading, so it matches the live panel.",
    aggRawNotice: "Plotted as received — {rows} readings, no aggregation.",
    aggRawCapped: "Raw plotting is capped at {cap} readings and this range holds {rows}, so the aggregated series is shown instead.",
    aggIntervalTooltip: "{n} readings, {start} – {end}",
    aggRangeTooltip: "lowest {min}, highest {max}",
    aggLatestTooltip: "latest reading",

    // Degradation trending (#130). Every figure is rendered next to the window
    // it was measured over, and nothing here may imply a failure date.
    dtTitle: "Degradation trend",
    dtLoading: "Loading trend…",
    dtError: "Could not load the trend",
    dtVelocity: "Drift velocity",
    dtAcceleration: "Change in velocity",
    dtAdjusted: "With temperature removed",
    dtDegPerDay: "°/day",
    dtOverWindow: "over {days} days",
    dtWindowDates: "{start} – {end}",
    dtVsPrevious: "against the previous {days} days, {start} – {end}",
    dtTempCoefficient: "{coef} °/°C, r = {r}, {min}–{max} °C in this window",
    dtTempNotAdjusted: "Temperature not removed",
    dtTempSource: "Temperature source",
    dtTempSourceWindow: "{sources}, over {start} – {end}.",
    dtTempSourceCrosses: "This window crosses a source boundary at {at}: it mixes an ERA5 reanalysis on a grid cell 3.2 km away with an Open-Meteo point estimate at the site. The two never ran side by side, so the offset between them was never measured and cannot be recovered now. The r above spans both and is not one measurement.",
    dtTempSourceSingle: "One source across the whole window, so the r above does not span a change of provider.",
    dtSourceEra5: "ERA5 reanalysis, 3.2 km away",
    dtSourceLive: "Open-Meteo point estimate",
    dtSourceUnlabelled: "rows with no recorded source",
    dtNotMeasurable: "Not measurable",
    dtAxes: "Per axis",
    dtFlag: "Trend indicator",
    dtFlagRaised: "Raised",
    dtFlagNotRaised: "Not raised",
    dtFlagUnassessed: "Not assessed",
    dtFlagUncalibrated: "uncalibrated threshold, shown without colour on purpose: no tower here has degraded yet, so there is no example to set it against",
    dtFitNote: "Fitted over {bins} of {total} bins of {span} h each, from {readings} readings. {excluded} uplinks carried no angle measurement and were rejected rather than counted as zero.",
    dtDisclaimer: "A trend, not a prediction. This says how fast drift is changing and whether that rate is itself changing. It is not a failure forecast and carries no date. Compared only against this tower's own past, never against another tower — mounting, exposure and sensor placement differ. Measured tilt also moves with temperature, which is why the third figure is there.",
    dtReasonTooFewReadings: "fewer than 50 readings in this window",
    dtReasonSparseCoverage: "under half the window's time bins held a reading",
    dtReasonNoFit: "the readings do not support a slope",
    dtReasonNoVelocity: "no velocity for this window",
    dtReasonNoPreviousWindow: "the window before this one has no velocity to compare with",
    dtReasonInsufficientHistory: "fewer than three earlier windows to compare against",
    dtReasonDegenerateSpread: "this device's earlier windows show no spread to compare against",
    dtReasonTooFewPairedBins: "too little of this window has a matching temperature reading",
    dtReasonUnknown: "reason not stated",

    // Authentication tiers
    login: "Login",
    logout: "Logout",
    loginArrow: "Login →",
    // Account menu behind the mobile avatar
    accountMenu: "Account",
    accountNotSignedIn: "Not signed in",
    tierGuest: "Guest",
    tierUser: "User",
    tierSuperuser: "Super user",
    lockedDriftTitle: "Login to view drift analysis",
    lockedAnalysisTitle: "Login to view detailed analysis",
    lockedAnomalies: "Login to view all anomalies",
    simulatedBannerGuest: "⚠ Simulated data — login to access full analytics",
    simulatedBannerUser: "⚠ Simulated data — contact admin to request super user access",

    // Per-node satellite snapshot (#109) — newest usable Sentinel-2 view of a
    // ~5 km box around a node.
    //
    // Every string here that states a date, a cloud percentage or a resolution
    // is load-bearing, not decoration. The map's own imagery resolves 0.5 m and
    // was photographed 2020-02-17; this is 10 m/pixel and days old. Strip the
    // qualifiers and
    // the two become indistinguishable, which is the whole failure this feature
    // is written to avoid. Do not shorten these to "Satellite image" when
    // editing.
    // The basemap's age, stated permanently under the snapshot (#82). Measured
    // against Esri's metadata service on 2026-09-06: photographed 2020-02-17,
    // 0.5 m, WorldView-2, drawn only to map level 17 — so the z18/z19 the map
    // allows are magnified, not sharper.
    //
    // HARDCODED, AND IT WILL GO STALE. Esri refreshes the mosaic on its own
    // cadence and nothing here checks. Re-verify with the command in
    // SATELLITE_IMAGERY.md 1 before relying on it, and correct this string.
    // The previous version of this claim said "0.22 m/pixel and from 2021" and
    // both halves were wrong: 0.22 m is the tile grid at zoom 19, and 2021 was
    // Esri's publication date rather than the capture.
    basemapVintage: "Basemap underneath: photographed 2020-02-17, 0.5 m resolution. Not current \u2014 verify before acting on it.",
    satSnapshotTitle: "Satellite snapshot",
    satSnapshotHint: "Newest usable Sentinel-2 view of a ~5 km box around this node.",
    satSnapshotFetch: "Fetch image",
    satSnapshotRefresh: "Refresh",
    satSnapshotLoading: "Fetching…",
    satSnapshotError: "Could not load the snapshot",
    satSnapshotCaptured: "Captured",
    satSnapshotToday: "today",
    satSnapshotDaysAgo: "{n} days ago",
    satSnapshotCloud: "Cloud cover",
    satSnapshotCloudScope: "(whole scene)",
    satSnapshotResolution: "Resolution",
    satSnapshotPixel: "pixel",
    satSnapshotResolutionNote: "a tower is under one pixel",
    satSnapshotSource: "Source",
    satSnapshotUnknown: "not reported",
    satSnapshotAlt: "Sentinel-2 true-colour image captured {date}, scene {id}",
    // Never silent: the UI says how many newer passes were too cloudy to use.
    satSnapshotOlder:
      "Older scene — the {n} newer passes were too cloudy to use.",
    satSnapshotNoScene: "No usable image",
    satSnapshotNoSceneBody:
      "Every Sentinel-2 pass in the last {days} days was above {pct}% cloud.",
    satSnapshotUnavailable: "Imagery status unknown",
    satSnapshotUnavailableBody: "The Copernicus catalogue could not be reached.",
    satSnapshotNoImage: "Scene found, image not available",
    satSnapshotNoImageBody:
      "The catalogue lists this scene but the image could not be rendered.",
    // A refused refresh is not an error: each one spends a Copernicus render,
    // so the panel says when the next one can be asked for.
    satSnapshotCooldown:
      "Refreshed a moment ago — a new image can be requested in {n} s.",

    // Sensor watchdog (#155) — which devices have stopped reporting.
    //
    // The wording here is load-bearing in the same way the satellite strings
    // above are. "Offline" is one word for three different failures, and the
    // value of this panel is entirely in telling them apart: a device that went
    // quiet needs a battery or a gateway, one that never reported needs its
    // provisioning finished, and one sending empty frames needs neither — its
    // radio link is fine. Do not collapse these into a single "Offline" label.
    watchdog: "Reporting watchdog",
    watchdogHint:
      "Whether each device is still reporting, measured against that device's own cadence rather than one threshold for the whole network.",
    watchdogLoading: "Checking devices…",
    watchdogError: "The watchdog could not be reached",
    watchdogErrorHint:
      "This is an absence of checking, not an all-clear. Nothing about the devices follows from it.",
    watchdogAllReporting: "All {n} devices reporting",
    watchdogFindings: "{n} of {total} devices need attention",
    // State names. Each says what happened, never only that something is wrong.
    watchdogStateReporting: "Reporting",
    watchdogStateSilent: "Stopped reporting",
    watchdogStateNeverReported: "Never reported",
    watchdogStateNotMeasuring: "Reporting, not measuring",
    watchdogStateUnknown: "Could not be checked",
    // Per-device detail.
    watchdogSilentBody: "Last reading {ago}. This device's own threshold is {threshold}.",
    watchdogNeverReportedBody:
      "Registered, with no reading at any time. Provisioning was never finished, and this will not resolve on its own.",
    watchdogNotMeasuringBody:
      "{empty} of the last {sampled} uplinks carried no measurement. The link works, so nothing watching for silence can see this.",
    watchdogUnknownBody:
      "This device's readings could not be read, so its state is unknown. It is not known to be healthy.",
    watchdogReportingBody: "Last reading {ago}. Threshold {threshold}.",
    watchdogEmptyNote: "{empty} of the last {sampled} uplinks carried no measurement.",
    watchdogSince: "Since {time}",
    watchdogThresholdFromCadence: "derived from this device's own cadence",
    watchdogThresholdFromFleet: "no cadence measurable — using the network-wide setting",
    // Stated permanently, because it is the gap this panel does NOT close.
    // The device list is the union of two registries. If one is unreadable a
    // device present only in it disappears from the list — and em320-tilt-sn099,
    // the device this feature exists to surface, is exactly such a device.
    watchdogListIncomplete: "This list may be incomplete",
    watchdogListIncompleteHint:
      "A device registry could not be read, so a device may be missing from this list rather than shown as healthy.",
    watchdogScopeNote:
      "A device whose reporting rate is falling but has not reached zero still reads as reporting. Declining-rate detection is not implemented.",

    // ── Online help (#171) ──────────────────────────────────────────────────
    //
    // The bodies <HelpTip> renders. Read helpTopics.js before editing any of
    // them — it is the registry that decides which of these appear together,
    // and which of them are shared with a caveat rendered elsewhere on screen.
    //
    // Five rules, and each of them is a defect this project has already had to
    // correct once:
    //
    //   * NOTHING HERE RESTATES A LABEL. Every string answers what is measured,
    //     in what units, from what source, over what window, and what the
    //     number is NOT. "Drift — shows drift" costs a tap, returns nothing and
    //     teaches the reader to stop opening the help; the next one they skip
    //     is the one that mattered.
    //
    //   * SHORT SENTENCES, ORDINARY WORDS, ONE IDEA EACH. These are read on a
    //     phone, in a hurry, often in a second language. The first draft of this
    //     block was accurate and unreadable: single paragraphs of seventy words
    //     with the correction buried in a subordinate clause at the end. A
    //     reader who gives up half way through has taken the measurement and
    //     left the caveat, which is the worse half to lose. Hence the `helpX` /
    //     `helpXNot` split — two short paragraphs, the second one always the
    //     limits of the first. Jargon is allowed when it is the real name of the
    //     thing (Sentinel-2, Isolation Forest, cadence_bias_c) and is explained
    //     in the same sentence; it is not allowed as a shortcut ("Euclidean
    //     norm" became "a tower that swings more also raises this number").
    //
    //   * ONE CLAIM, ONE STRING. helpNdviClaim is rendered by the corridor
    //     segment layer, by the "Connect your GIS" panel and by two help
    //     topics — one string, four places. Adding a fifth wording of it would
    //     restore exactly the ogcCaveat/segmentLayerCaveat problem this
    //     replaced. Before writing a new key here, check whether the claim
    //     already exists and add its key to the topic's bodyKeys instead.
    //
    //   * THE FIGURES BELOW SUPERUSER ARE GENERATED, AND THE HELP LEADS WITH
    //     IT. helpSimulatedLead is prepended, never appended: an explanation of
    //     what a drift value means, shown over a fabricated number, converts a
    //     deliberate placeholder into an apparent measurement.
    //
    //   * SPECIFIC FACTS, NOT REASSURANCE. The dates, the 14 days, the 10 m
    //     per pixel, the 2026-07-28 join and cadence_bias_c are all load
    //     bearing and all verifiable against API.md, WEATHER_DATA.md and
    //     lambda_function.py. Do not soften them into "approximately" when
    //     editing — and when one of them stops being true, correct it here.
    helpAria: "Help: {label}",
    helpSimulatedLead:
      "⚠ These numbers are not real. They are generated for the demo. No sensor measured them, and they say nothing about the real power line.",
    // Short noun phrases, used only for the help control's aria-label where the
    // object on screen has no label key of its own.
    helpLabelBaseline: "Baseline",
    helpLabelDeviceStatus: "Device status",
    helpLabelCorridorNdvi: "Corridor NDVI",

    // Shared by every topic whose figure can render as "?". #33 and #57 are
    // both the same mistake: an absence of checking shown as an absence of
    // hazard.
    helpUnknownValue:
      "A question mark means we have no answer: the check did not run, or it failed. It is not a zero, and it is not an all-clear.",

    helpFiresCount:
      "How many hot spots the NASA FIRMS satellites (VIIRS and MODIS) found inside the monitored area in the last {hours} hours. A hot spot is one pixel that was much warmer than the ground around it.",
    helpFiresCountNot:
      "A hot spot is not a confirmed fire. Factories, machines and sunlight on water look the same from orbit. The satellites also pass only a few times a day, so a low number is not the same as someone watching.",

    helpWatchdogCoverage:
      "Whether each device is still sending data. Every device is measured against its own normal rhythm, worked out from its past messages. These devices report at very different speeds, so one rule for the whole network would be wrong.",
    helpWatchdogCoverageNot:
      "Some devices send in bursts, so they send more than the estimate expects. Their uptime in the corridor report can then rise above 100%. That means the estimate was low, not that something is broken — read “coverage” there instead.",

    // The claim. Rendered by SegmentNdviLayer, by the GIS panel, and inside two
    // help topics. One string.
    helpNdviClaim:
      "NDVI measures how green the ground is inside the corridor. It does not measure how tall the plants are, how close they are to the wires, or how dangerous they are.",
    helpCorridorNdvi:
      "This value comes from the Sentinel-2 satellite at 10 m per pixel, averaged over the whole corridor — about 180 km². Always read the date beside it: the check runs once a week, and clouds can leave an older image in place.",
    helpCorridorNdviNot:
      "One average over a large area says nothing about a single span between two towers. And if the value falls, the cause is unknown: fire, cutting, drought, harvest and grazing all look the same from space.",
    helpSegmentNdvi:
      "The same measurement, but made separately for each span between two towers, so a change can be located. Each span shows how old its value is. A span with no value says why: clouds, outside the satellite image, or the check failed.",

    helpDriftValue:
      "How far this tower has moved from its own starting position, in degrees, from the tilt sensor's X, Y and Z angles. “Current drift” is the newest reading in the range. “Peak drift” is the largest one in it.",
    helpDriftValueNot:
      "It is not the absolute angle of the tower, and it is not a lean in one direction: a tower that swings more in the wind also raises this number. Compare it only with this tower's own past — mounting and exposure differ from tower to tower.",
    helpDriftBaseline:
      "The starting position of this device: the average of its first 14 days of readings, one value per axis, in degrees. It is fixed when the device is installed and never recalculated. Every drift value on this chart is measured from it.",
    helpDriftBaselineNot:
      "It is not a correct or vertical position. If the sensor was mounted crooked, the baseline simply records that as normal. Readings that triggered the hardware, and readings with no angle, are left out — the dates and counts are printed under the figures.",

    helpMlAnomaly:
      "Two detectors write to this list. The first looks for readings that are extreme on their own. The second learns what is normal for each tower from its last 30 days. It uses an Isolation Forest, retrained every night, and flags readings that are unusual for that tower.",
    helpMlAnomalyNot:
      "A “medium” row always comes from the second detector, and it is deliberately quiet: no email, no alarm. It only scores readings that did not already trigger the first detector, so it never re-judges something the hardware has flagged. “low” is every motion event and stays hidden until you ask for it.",
    helpAnomalyConfidence:
      "The detector's own score for this one detection, from 0 to 1, shown here as a percentage.",
    helpAnomalyConfidenceNot:
      "It is not a probability. 80% does not mean four detections in five were real — these scores have never been checked against what actually happened. Most of them also land near the top of the scale, so they separate detections only roughly. Use it to compare detections from the same detector, and for nothing else.",

    helpTemperatureStats:
      "Air temperature over the same period as the trend, in °C. It is averaged hour by hour, not message by message. The devices changed how often they report, and a plain average would give the busier period too much weight. The API publishes that difference as cadence_bias_c.",
    helpTemperatureStatsNot:
      "It is not one single source. Until 28 July 2026 it came from the ERA5 model, on a grid cell 3.2 km away. After that date it comes from an Open-Meteo estimate at the site itself. The two never ran at the same time, so nobody ever measured the gap between them — a period crossing that date mixes both.",

    helpSatelliteSnapshot:
      "A Sentinel-2 image at 10 m per pixel, covering about 5 km around this node. It is the newest pass that was clear enough to use, so the date belongs to the image and not to today. Cloudy passes are skipped, which is why a usable image can be weeks old.",
    helpSatelliteSnapshotNot:
      "A tower is smaller than one pixel here. You can see the ground around the node, not the structure itself.",

    helpDeviceStatus:
      "The worst state among all sensors in this node. Green: reporting normally. Orange: battery low. Grey: nothing received for longer than the offline limit in Settings. Red: an alert.",
    helpStatusUnknown:
      "Pale grey — a hollow ring on the map — means something different: no health record answered for this device, so we could not decide. It never means healthy and it never means offline. If one sensor of a node is missing from the answer, the node shows this and names that sensor.",
    // Line rating on the weather station node (#247). Indicative, one span.
    dlrTabWeather: "Weather",
    dlrTab: "Line rating (DLR)",
    // #255. The chip says a word everyone knows at a glance; the tooltip says
    // the thing that is actually true, which is narrower than "unfinished".
    betaBadge: "Beta",
    betaBadgeWhy: "Beta: indicative only. This figure describes one span, not the whole line, and has never been checked against a measured conductor temperature. Do not dispatch against it.",
    dlrTitle: "Line rating at this span (indicative)",
    dlrSubtitle: "Dynamic line rating from the weather measured at this station. It describes this span only, not the whole line, and is not a rating to dispatch against.",
    dlrSimulated: "Example data. The settings and the rating below show how the panel works and describe no real line.",
    dlrLoadError: "Could not load the line rating",
    dlrNotConfigured: "No rating yet. Enter the line settings below: conductor, maximum conductor temperature, static rating, span direction and elevation.",
    dlrInvalidSettings: "The saved line settings are no longer valid, so no rating is shown. Check them below and save again.",
    dlrUnknownStale: "No rating: the latest complete station reading is {age} min old, and the limit is {max} min. A rating from old weather would describe a different hour.",
    dlrUnknownNoReading: "No rating: the station has sent no reading with both air temperature and wind speed in the last 6 hours.",
    dlrUnknownNoPosition: "No rating: this node has no position, which the sun's angle depends on.",
    dlrUnknownMissingField: "No rating: the latest reading is missing a value the calculation needs.",
    dlrUnknownOther: "No rating could be computed.",
    dlrSettingsTitle: "Line settings",
    dlrEdit: "Edit",
    dlrView: "View",
    dlrFooter: "Indicative only. Computed for this span with the settings above and the station's wind at its own height. Not validated against a measured conductor temperature.",
    dlrRatingAt: "Rating at {t} °C",
    dlrStatic: "Static rating",
    dlrRatio: "Compared with static",
    dlrSunAlone: "Rating 0 A: in this calm air the sun alone heats the conductor to about {temp} °C, above its {tmax} °C limit, before any current flows.",
    dlrBelowStatic: "Below the static rating. This is normal in calm air: static ratings usually assume a steady breeze, and right now there is less cooling than that.",
    dlrWeatherUsed: "Weather used",
    dlrWindAtStation: "Wind at the station ({h} m)",
    dlrAttack: "Wind angle to the conductor",
    dlrAttackMeasured: "{deg}°, measured",
    dlrAttackAssumed: "{deg}°, assumed: below {v} m/s the wind direction is not reliable",
    dlrReadingAge: "Reading taken",
    dlrMinutesAgo: "{n} min ago",
    dlrHeatBalance: "{method}. At the maximum temperature, per metre of conductor: convection cools {conv} W, radiation cools {rad} W, the sun heats {sun} W.",
    dlrHeightCorrected: "With a height correction for {terrain} terrain (wind × {factor}, {wind} m/s at the conductor), the rating would be {amps} A. That is an assumption about the wind profile, not the rating above.",
    dlrLoadTitle: "Present load (optional)",
    dlrLoadByCurrent: "Current",
    dlrLoadByPower: "P, Q and voltage",
    dlrLoadCurrent: "Line current",
    dlrLoadVoltage: "Voltage, line to line",
    dlrLoad: "Load",
    dlrUtilisation: "Of the rating",
    dlrHeadroom: "Headroom",
    dlrConductorTemp: "Conductor, estimated",
    dlrCompareRating: "Rating",
    dlrRowStatic: "Static rating",
    dlrRowAar: "Ambient-adjusted",
    dlrRowDlr: "Dynamic, now",
    dlrGainOverStatic: "The weather measured at this span gives {amps} A more headroom than the static rating right now.",
    dlrLoadBelowStatic: "The weather measured at this span puts the rating {amps} A below the static rating right now. The static rating assumes conditions better than these.",
    dlrAarNote: "Ambient-adjusted uses the measured air temperature with the {wind} m/s wind the static rating assumes. The step from it to the dynamic figure is what measuring the wind here is worth; the step from the static rating to it was already in your own rating.",
    dlrLoadPrivate: "What you type here stays in this browser. It is not sent to the server or stored.",
    dlrSetConductor: "Conductor",
    dlrConductorCustom: "Custom conductor",
    dlrCustomDiameter: "Outer diameter",
    dlrCustomStrand: "Outer wire diameter",
    dlrCustomRLow: "AC resistance at the lower temperature",
    dlrCustomTLow: "Lower temperature",
    dlrCustomRHigh: "AC resistance at the upper temperature",
    dlrCustomTHigh: "Upper temperature",
    dlrSetTmax: "Maximum conductor temperature",
    dlrSetStatic: "Static rating",
    dlrSetAzimuth: "Span direction from north",
    dlrSetElevation: "Elevation",
    dlrSetEmissivity: "Emissivity",
    dlrSetAbsorptivity: "Solar absorptivity",
    dlrSetStationHeight: "Station height",
    dlrSetConductorHeight: "Conductor height",
    dlrSetStaticWind: "Wind assumed by the static rating",
    dlrAzimuthPrefill: "The nearest mapped line runs at {deg}° here. Check it against the span: at a tower where the line turns, the two spans differ.",
    dlrAzimuthUse: "Use this direction",
    dlrAzimuthOffCorridor: "This node is marked as not on the monitored line, so the nearest mapped line may not be the one at this station.",
    dlrSetMethod: "Calculation method",
    dlrSetHeightCorrection: "Wind height correction",
    dlrTerrain_off: "Off (wind as measured)",
    dlrTerrain_open: "Open terrain",
    dlrTerrain_scattered: "Scattered obstacles",
    dlrTerrain_rough: "Rough terrain",
    dlrLastChanged: "Last changed by {who} on {when}, version {version}",
    dlrSaveConflict: "Someone else changed these settings in the meantime. Reload the panel and try again.",
    dlrSaving: "Saving…",
    dlrSave: "Save settings",
    dlrReadOnly: "Only a superuser can change these settings.",
    dlrErrRange: "Must be between {min} and {max}",
    dlrErrRequired: "Required",
    dlrErrNumber: "Enter a number",
    dlrErrUnknown: "Not a recognised value",
    dlrErrStrand: "The wire must be smaller than half the conductor",
    dlrErrNotAboveLow: "Must be higher than the lower value",
  },

  me: {
    // Top bar
    searchPlaceholder: "Pretraži SAMST mrežu",

    // Category buttons
    catAnomalies: "Anomalije",
    catTiltSensors: "Senzori nagiba",
    catMotionSensors: "Senzori pokreta",
    catTempSensors: "Temperaturni senzori",
    catWeatherStations: "Meteo stanice",
    catPowerMeters: "Električna mjerila",
    // Short forms for the mobile chip row, where the full labels overflow the
    // viewport. The icon already carries the "sensor" meaning.
    catTiltShort: "Nagib",
    catMotionShort: "Pokret",
    catTempShort: "Temp.",
    catWeatherShort: "Meteo",
    catPowerShort: "Struja",

    // Anomalies panel
    detectedAnomalies: "Otkrivene anomalije",
    noAnomalies: "Nema otkrivenih anomalija",
    allSystemsNormal: "Svi sistemi su normalni",
    anomaliesError: "Greška pri učitavanju anomalija.",
    rawPayload: "Sirovi podaci",
    confidence: "pouzdanost",
    weatherNotChecked: "Vrijeme nije provjereno za ovu detekciju",
    showMotionEvents: "Prikaži događaje pokreta",
    loadMore: "Učitaj još",
    moreCount: "+{n} više",
    clickToExpand: "kliknite za prikaz svih",
    today: "Danas",
    yesterday: "Juče",
    unknownDate: "Nepoznat datum",
    loading: "Učitavanje...",

    // Sažimanje ponovljenih anomalija (#211)
    rollupTitle: "{count} očitanja sažeta u jedan unos",
    rollupVariants: "{count} različitih poruka",
    rollupTruncated: "Još ih se nastavlja nakon ove stranice",

    // Redovi radne liste (#208)
    anomalyTypeTilt: "nagib",
    anomalyTypeMl: "nagib (ML)",
    anomalyTypeTemperature: "temp.",
    anomalyTypeMotion: "pokret",
    anomalyTypeMounting: "montaža",
    anomalyTypeFlood: "poplava",
    towerEntries: "{n} stavki na ovom stubu",
    rollupFolded: "Očitanja sažeta u ovaj red",
    rollupShowing: "najnovijih {shown} od {count}",

    // Node info panel
    nodeInfo: "Informacije o čvoru",
    sensorData: "Podaci senzora",
    loadingSensorData: "Učitavanje podataka senzora...",
    failedToLoadSensor: "Greška pri učitavanju podataka senzora.",
    readings: "Očitavanja",
    hiddenChannels: "{n} neidentifikovanih kanala nije prikazano",
    close: "Zatvori",
    backToMap: "← Nazad na mapu",

    // Sensor fields
    fieldSensor: "Senzor",
    fieldTimestamp: "Vremenska oznaka",
    fieldType: "Tip",

    // Health status
    statusOk: "Na mreži",
    statusWarning: "Baterija slaba",
    statusOffline: "Van mreže",
    statusAlert: "Upozorenje",
    statusUnknown: "Status nepoznat",
    lastSeen: "Posljednji put viđen",
    // These cannot be built by appending a suffix to a number, which is why the
    // old "h ranije" key is gone rather than joined by "min ranije"/"d ranije".
    //
    // Montenegrin is ijekavian — "prije", not the ekavian "pre" that Intl's
    // sr-Latn data returns. After "prije" the numeral governs the genitive, so
    // "minut" and "dan" happen to be invariant here ("prije 1 minuta",
    // "prije 5 minuta") and only "sat" changes: genitive singular "sata" for
    // 1-4, genitive plural "sati" from 5 up. The one/few/other split is kept
    // for all three units anyway, so the invariance stays deliberate rather
    // than accidental if a unit is ever added.
    lastSeenJustNow: "upravo sada",
    lastSeenMinutesOne: "prije {n} minuta",
    lastSeenMinutesFew: "prije {n} minuta",
    lastSeenMinutesOther: "prije {n} minuta",
    lastSeenHoursOne: "prije {n} sata",
    lastSeenHoursFew: "prije {n} sata",
    lastSeenHoursOther: "prije {n} sati",
    lastSeenDaysOne: "prije {n} dana",
    lastSeenDaysFew: "prije {n} dana",
    lastSeenDaysOther: "prije {n} dana",
    battery: "Baterija",

    // Health / config availability
    healthDataUnavailable: "Podaci o stanju nisu dostupni — status senzora je nepoznat.",
    healthRetrying: "Ponovni pokušaj svake minute.",
    nodesLoadError: "Nije moguće učitati čvorove — mapa je možda nepotpuna.",
    nodesLoadErrorHint: "Osvježite stranicu da pokušate ponovo.",
    nodesSaveError: "Izmjena nije sačuvana.",
    nodesSaveErrorHint: "Prikazana je samo ovdje i nestaće nakon osvježavanja.",
    // Brisanje čvora
    delete: "Izbriši",
    deleteNode: "Izbriši čvor",
    deleteNodeConfirm: "Izbrisati ovaj čvor?",
    deleteNodeWarning: "Ovo uklanja čvor i njegovih {n} dodijeljenih senzora sa mape. Radnja se ne može poništiti.",
    noHealthDataForDevice: "Nema podataka o stanju za ovaj uređaj.",
    noHealthDataForSensors: "Nema podataka o stanju za: {list}",
    sensorHealthError: "Nije moguće učitati stanje senzora.",
    configDegradedBanner: "Servis konfiguracije je degradiran.",
    configDegradedHint: "Prikazani pragovi možda nisu ažurni.",
    configDegradedBadge: "Degradirana konfiguracija",
    configLoadError: "Podešavanja nije moguće učitati",
    configDegradedNotice: "Ovo su rezervne vrijednosti, a ne sačuvana podešavanja. Izmjena je onemogućena dok se servis konfiguracije ne oporavi.",
    weatherStatusUnavailable: "Status vremena nepoznat — nijedan izvor nije dostupan.",
    weatherStatusPartial: "Provjera vremena nepotpuna — jedan izvor nije dostupan.",
    // Detalji vremenskog upozorenja (#92)
    weatherAlertCount: "{n} aktivnih upozorenja",
    weatherAlertCountOne: "1 aktivno upozorenje",
    weatherAlertStartUnknown: "početak nepoznat",
    weatherAlertNoExpiry: "kraj nije naveden",
    weatherAlertRegions: "Regije: {list}",
    weatherAlertRegionsUnrecorded: "{n} regionalnih upozorenja — regije nisu zabilježene",
    weatherAlertShowDetail: "Prikaži detalje upozorenja",
    weatherAlertHideDetail: "Sakrij detalje upozorenja",

    // Detekcije požara (#93) — NASA FIRMS satelitske termalne anomalije.
    // Isto pravilo kao u engleskoj verziji: uvijek "termalna anomalija", nikada
    // "potvrđeni požar", i odsustvo detekcije nije dokaz da požara nema.
    catFires: "Detekcije požara",
    catFiresShort: "Požari",
    fireThermalAnomaly: "Termalna anomalija",
    fireNotConfirmed: "Satelitska detekcija — nije potvrđeni požar",
    fireConfidenceLabel: "Pouzdanost",
    fireConfidence_low: "niska",
    fireConfidence_nominal: "nominalna",
    fireConfidence_high: "visoka",
    fireConfidencePct: "{n}%",
    fireConfidenceUnknown: "nije prijavljena",
    fireAcquired: "Zabilježeno",
    fireAgeMinutes: "prije {n} min",
    fireAgeHours: "prije {n} h",
    fireAgeDays: "prije {n} d",
    fireAgeUnknown: "vrijeme nepoznato",
    fireDistanceToNode: "{km} km od {node}",
    fireWithinRadius: "unutar {km} km",
    fireDistanceUnknown: "Rastojanje do čvorova nepoznato — lista čvorova nije dostupna.",
    fireNoNodes: "Nema čvorova na karti za mjerenje rastojanja.",
    fireRadiativePower: "Snaga zračenja",
    fireCellCentre: "Centar ćelije",
    fireCellSize: "~{m} m ćelija detekcije",
    fireBannerNear: "Termalna anomalija {km} km od {node}, {age} — pouzdanost: {confidence}. Satelitska detekcija, nije potvrđeni požar.",
    fireBannerSomeFar: "{n} termalnih anomalija u posljednjih {hours} h, nijedna unutar {km} km od čvora. Detekcije, nisu potvrđeni požari.",
    fireBannerNone: "Nema termalnih anomalija u posljednjih {hours} h. Sateliti prelaze nekoliko puta dnevno — ovo nije dokaz da požara nema.",
    fireBannerStale: "Status detekcije požara nepoznat — posljednja provjera prije {n} min.",
    fireBannerPartial: "Provjera požara nepotpuna — {failed} od {total} satelitskih izvora nije dostupno. {n} termalnih anomalija iz izvora koji su odgovorili.",
    fireBannerUnavailable: "Status detekcije požara nepoznat — FIRMS nije dostupan.",
    fireBannerUnavailableReason: "Status detekcije požara nepoznat — {reason}.",
    fireBannerClipped: "Detekcije dopiru do ruba nadgledanog područja, pa je ovo broj unutar njega, a ne ukupan broj.",
    fireCountBounded: "Stvarni broj je veći. Tople tačke su nađene tačno na ivici područja o kojem pitamo, pa se brojanje tu zaustavlja. Ono što je izvan njega nije ni provjereno.",

    // Satelitski indikator vegetacije (#103). Nijedna poruka ne imenuje uzrok:
    // NDVI pada zbog požara, sječe, suše, žetve i ispaše podjednako i ne može
    // ih razlikovati — vidi englesku verziju.
    ndviBannerNormal: "Vegetacija normalna — {date}, NDVI {value}.",
    ndviBannerModerateStress: "Stres vegetacije — {date}, NDVI {value}. Prosjek za područje; uzrok nije utvrđen.",
    ndviBannerHighStress: "Visok stres vegetacije — {date}, NDVI {value}. Prosjek za područje; uzrok nije utvrđen.",
    ndviBannerSuddenDrop: "NDVI pao za {drop} za {days} dana — {date}, sada {value}. Uzrok nije utvrđen: požar, sječa i suša izgledaju isto.",
    ndviBannerNoScene: "Vegetacija nije osmotrena — nema snimka bez oblaka. Ovo ne govori ništa o koridoru.",
    ndviBannerNoSceneReason: "Vegetacija nije osmotrena — {reason}. Ovo ne govori ništa o koridoru.",
    ndviBannerStale: "Status vegetacije nepoznat — posljednja provjera prije {n} dana.",
    ndviBannerUnavailable: "Status vegetacije nepoznat — Copernicus nije dostupan.",
    ndviBannerUnavailableReason: "Status vegetacije nepoznat — {reason}.",
    ndviPartialView: " Vidljivo samo {pct}% područja.",
    ndviDateUnknown: "datum nepoznat",

    groundStability: "Stabilnost tla (radar)",
    groundStabilityLoading: "Provjera…",
    groundStabilityError: "Nije moguće učitati radarsko očitavanje.",
    groundStabilityUnavailable: "Još nema radarskog očitavanja za ovaj čvor.",
    groundStabilityNotMeasured: "Nije izmjereno u posljednjem prolazu — tlo oko čvora je izgubilo koherenciju. Ovo ne govori ništa o stubu.",
    groundStabilityFirst: "Koherencija {value} · {window}. Prvo očitavanje — još nema sa čim uporediti.",
    groundStabilityOk: "Koherencija {value} · {window}. U skladu sa nedavnim očitavanjima ovog čvora.",
    groundStabilityDrop: "Koherencija {value} · {window} — pad od {drop} u odnosu na uobičajenih {median}. Nešto se ovdje promijenilo; ovo nije mjerenje pomjeranja i uzrok nije utvrđen.",
    // {weather} ostaje na engleskom: to je tekst upozorenja onako kako ga je
    // Meteoalarm zapisao, a naknadno prevođenje imena upozorenja bilo bi nagađanje.
    groundStabilitySuspect: "Koherencija {value} · {window} — pad od {drop} u odnosu na uobičajenih {median}, ali je tokom ovog para zabilježena jaka kiša ({weather}). Voda koja se zadržava na površini odbija radarski signal od satelita, a tlo se vraća u normalu kada se osuši, pa ovaj par ne može razlikovati promjenu tla od mokre površine. Odlučuje sljedeći par bez takvog upozorenja.",
    groundStabilityWeather: " Vrijeme tokom ovog para: {weather}.",
    groundStabilityWeatherUnnamed: "detalji upozorenja nisu zabilježeni",
    groundStabilityStale: " Posljednja provjera prije {n} dana.",
    groundStabilityDatesUnknown: "datumi nepoznati",

    // Identitet voda na panelu čvora (#209).
    lineIdentity: "Vod",
    lineCircuit: "1 sistem",
    lineCircuits: "{n} sistema",
    lineCable: "1 provodnik",
    lineCables: "{n} provodnika",
    lineOperator: "operator {name}",
    lineSource: "Najbliži kartirani vod, udaljen {distance}.",
    lineNotOnCorridor: "Ovaj čvor nije na nadziranom koridoru, pa ovaj vod nije nužno njegov sistem.",

    ndviBannerReading: "NDVI koridora {value} · {date} · prosjek za područje",
    ndviBannerReadingStale: " · nije ažurirano {n} d",

    // NDVI po dionicama koridora, WMS sloj sa GeoServer-a (#146).
    catSegmentNdvi: "Dionice koridora (NDVI)",
    catSegmentNdviShort: "Dionice",
    // Ranije `segmentChipTitle`; sada početak niza `helpSegmentNdvi` (#171).
    segmentLayerTitle: "Dionice koridora — NDVI",
    // Ranije `segmentLayerCaveat`; sada `helpNdviClaim` na dnu ovog bloka —
    // jedna tvrdnja, jedan niz, isti i za sloj i za GIS panel i za pomoć (#171).
    segmentLabel: "Dionica {n}",
    segmentAgeToday: "izmjereno danas",
    segmentAgeDays: "izmjereno prije {n} d",
    segmentCarriedForward: "od tada nema upotrebljivog snimka",
    segmentPartialView: "Vidljivo samo {pct}% dionice.",
    segmentVsBaseline: "{delta} u odnosu na osnovnu vrijednost ove dionice za mjesec ({n} osm.)",
    segmentNoBaseline: "Za ovu dionicu još nema osnovne vrijednosti.",
    segmentNoValueCloud: "nema vrijednosti — oblačnost",
    segmentNoValueOutside: "nema vrijednosti — izvan svih snimaka",
    segmentNoValueUnavailable: "nema vrijednosti — provjera nije uspjela",
    segmentNoValueNever: "nikada nije izmjereno",
    segmentNoneDefined: "Još nije definisana nijedna dionica koridora.",
    segmentDetailsUnavailable: "Vrijednosti dionica nije moguće učitati.",

    // Online pomoć (#172)
    helpManual: "Uputstvo za operatera — otvara se u zasebnom prozoru",

    // Sidebar
    sensorList: "Lista senzora",
    settings: "Podešavanja",
    about: "O platformi",
    healthThresholds: "Pragovi zdravstvenog nadzora",
    offlineAfter: "Van mreže nakon",
    batteryWarningBelow: "Upozorenje za bateriju ispod",
    failedToSave: "Greška pri čuvanju. Pokušajte ponovo.",
    aboutDescription: "Sistem pametnog nadzora sredstava za prenosnu infrastrukturu.",
    poweredBy: "Pokreće LoRaWAN senzore, AWS i React.",

    // Izvještaj o koridoru (#143)
    corridorReport: "Izvještaj o koridoru",
    reportPeriodPrompt: "Izaberite period, zatim pritisnite Primijeni.",
    reportPeriodSelected: "Period: {start} → {end}",
    reportDownloadPdf: "PDF",
    reportDownloadCsv: "CSV",
    reportAssembling: "Sastavljanje izvještaja…",
    reportDownloading: "Preuzimanje…",
    reportDone: "Završeno",
    reportFailed: "Izvještaj nije mogao biti napravljen. Pokušajte ponovo.",
    reportEmptyPeriodNote: "Pokriva cijeli koridor. Period bez očitavanja i dalje daje izvještaj — uređaji koji nisu poslali ništa su navedeni, nisu izostavljeni.",

    // Povežite svoj GIS (#164)
    ogcTitle: "Povežite svoj GIS",
    ogcIntro: "Koridor je objavljen kao standardni OGC sloj. Dodajte ga u GIS koji već koristite i ažuriraće se sam — vaš softver čita uživo sa ovog servera.",
    ogcEndpointLabel: "WMS / WFS adresa",
    ogcCopy: "Kopiraj",
    ogcCopied: "Kopirano",
    ogcCopyManual: "Adresa je označena — pritisnite Ctrl+C da je kopirate.",
    ogcEndpointHint: "Jedna adresa za obje usluge; klijent sam dodaje WMS ili WFS. Klijent koji podržava samo WFS može koristiti /geoserver/wfs.",
    ogcVerify: "Provjerite u pregledaču",
    ogcVerifyHint: "Adresa iznad je za GIS klijent, a ne za pregledač — otvorena sama vraća grešku, što je ispravno. Ovaj link dodaje ono što bi klijent poslao, pa server odgovara svojim opisom obje usluge.",
    ogcLayersLabel: "Slojevi",
    ogcLayerLatest: "Trenutno stanje — jedan red po dionici, sa starošću vrijednosti i razlogom kada je nema.",
    ogcLayerSeries: "Cijela istorija, oko 11 godina Sentinel-2 snimaka — nosi WMS TIME dimenziju, pa vam vremenski kontrolor u QGIS-u daje izbor datuma bez ikakvog podešavanja.",
    ogcClients: "I QGIS i ArcGIS dodaju OGC adresu direktno: bez dodatka, bez projektnog fajla, bez izvoza koji se mora održavati.",
    // Prikazuje se odmah nakon helpNdviClaim, koji nosi samu tvrdnju.
    ogcCaveatGis: "Kada ovo postane sloj u vašem GIS-u, nijedno od tih objašnjenja ne ide sa njim.",

    // Dialog
    assignSensor: "Dodijeli senzor pinu",
    selectSensor: "Izaberi senzor",
    // Prikazuje se u samom dijalogu, uz izbor koji ga je izazvao (#199).
    sensorAlreadyOnNode: "Ovaj senzor je već dodijeljen čvoru {node}.",
    areaName: "Naziv područja (opciono)",
    areaPlaceholder: "npr. Podgorica Sjever",
    cancel: "Otkaži",
    addPin: "Dodaj pin",

    // Registracija end node-a
    registerDevicePrompt: "Uređaj nije na listi? Registruj ga",
    registerDevicePromptEmpty: "Još nema uređaja — registruj jedan",
    registerDeviceTitle: "Registruj end node",
    registerDeviceHint: "Govori SAMST-u da uređaj postoji. Ne podešava ništa u TTN-u — uređaj već mora da emituje.",
    registerDeviceId: "ID uređaja (kao u TTN-u)",
    registerDeviceType: "Tip senzora",
    registerDeviceLabel: "Naziv (opciono)",
    register: "Registruj",
    registering: "Registracija…",
    noDataYetShort: "još nema podataka",
    noDataYetTitle: "Još nema očitavanja",
    noDataYetBody: "Uređaj je registrovan ali se još nije javio. Očitavanja se pojavljuju kada se dekodira prvi uplink.",
    registeredOn: "Registrovan",

    // Drift chart
    driftChartLoading: "Učitavanje podataka…",
    driftChartNoData: "Nema očitavanja u ovom vremenskom opsegu.",
    driftChartError: "Greška pri učitavanju",
    range24h: "24 h",
    range7d: "7 dana",
    range30d: "30 dana",
    // Pogodan opseg, a ne ograničenje (#184, #231).
    range6m: "6 mjeseci",
    rangeCustom: "Prilagođeno",
    customFrom: "Od",
    customTo: "Do",
    // Postavlja početni datum na donju granicu podataka, 1. januar 2026 (#231).
    fromStart: "Od početka",
    fromStartTitle: "Postavi početni datum na 1. januar 2026, najraniji podatak koji sistem ima",
    apply: "Primijeni",
    baselineX: "Osnova X",
    baselineY: "Osnova Y",
    baselineZ: "Osnova Z",
    peakDrift: "Vršni pomak",
    currentDrift: "Trenutni pomak",
    xAxis: "Osa X",
    yAxis: "Osa Y",
    zAxis: "Osa Z",
    alertThreshold: "Prag",
    baselineWarning: "Osnova izračunata iz {n} očitavanja (manje od 24). Stabilizovaće se kako pristižu novi podaci.",
    spikeNoticeOne: "1 neplauzibilno očitavanje filtrirano iz grafikona — sačuvano u sirovim podacima",
    spikeNotice: "{n} neplauzibilnih očitavanja filtrirano iz grafikona — sačuvano u sirovim podacima",
    // Prekidi u prikupljanju podataka (#167).
    ingestGapLost: "⚠ Nedostaju podaci: prikupljanje je zaustavljeno {d} u ovom opsegu, a {u} je izvan roka čuvanja mreže i ne može se vratiti. Linija je povučena pravo preko toga.",
    ingestGapUnknown: "⚠ Prikupljanje je zaustavljeno {d} u ovom opsegu, a oporavak nije mogao biti provjeren. Očitavanja u osjenčenom rasponu možda nedostaju.",
    ingestGapRecovered: "Prikupljanje je zaustavljeno {d} u ovom opsegu. Sva očitavanja su vraćena.",
    ingestGapLegend: "Prekid prikupljanja",
    updated: "Ažurirano",
    // Izvoz izvještaja o pomaku (#141). PDF kruži bez sesije u kojoj je nastao,
    // pa zaglavlje mora navesti čime je uslovljen svaki broj.
    exportTitle: "Izvještaj o pomaku",
    exportDeviceId: "ID uređaja",
    exportedAt: "Izvezeno",
    exportRangeLabel: "Opseg",
    exportBaselineLine: "Osnova — X: {x}°  Y: {y}°  Z: {z}°",
    exportAlertPoints: "Tačaka s prelaskom praga: {n} od {total}",
    exportPeakDrift: "Vršni pomak: {v}°",
    exportThreshold: "Prag: ±{v}°",
    exportSpikeOne: "1 očitavanje filtrirano kao skok — prolazni izlet, isključen iz pomaka.",
    exportSpikeMany: "{n} očitavanja filtrirano kao skokovi — prolazni izleti, isključeni iz pomaka.",
    exportSpikeCaveat: "Vršni pomak iznad je iz filtrirane serije.",
    exportSpikeNone: "Nijedno očitavanje nije filtrirano kao skok u ovom opsegu — vršni pomak iznad je iz pune serije.",
    // SenseCAP S2120 meteo stanica (#77)
    wsAirTemperature: "Temperatura vazduha",
    wsHumidity: "Vlažnost",
    wsPressure: "Pritisak",
    wsWindSpeed: "Brzina vjetra",
    wsWindDirection: "Smjer vjetra",
    wsIlluminance: "Osvijetljenost",
    wsUvIndex: "UV indeks",
    wsWindGust: "Najjači udar vjetra",
    wsRain: "Padavine",
    wsRainRate: "Intenzitet padavina",
    wsRain24h: "Padavine, posljednja 24 h",
    wsRainRateNote: "Intenzitet padavina je vrijednost koju stanica sama daje za trenutno mjerenje. Skače već pri jednom prevrtanju posude kišomjera, pa ga treba čitati zajedno sa ukupnom količinom za 24 h. Ukupna količina označena sa ≥ ne obuhvata interval koji stanica nije mogla izbrojati.",
    wsBattery: "Baterija",
    wsJustNow: "upravo sada",
    wsAgo: "prije",
    wsUnverifiedNote: "Stanica emituje i kanale čije skaliranje još nije potvrđeno. Čuvaju se, ali se ne prikazuju kao mjerenja:",
    // Grafikoni istorije meteo stanice (#85)
    wsHistoryTitle: "Istorija mjerenja",
    wsNoParameterSelected: "Izaberite parametar za prikaz",
    wsWindDirectionNote: "Prikazano kao tačke, ne kao linija — smjer koji prelazi sjever inače crta skok pune visine između 359° i 1°.",
    wsChartRainNote: "Padavine su količina koja je pala u svakom prikazanom intervalu, prema kišomjeru stanice. Intenzitet padavina se ne prikazuje na grafikonu jer skače već pri jednom prevrtanju posude kišomjera; prikazan je kao trenutna vrijednost u panelu iznad.",
    // Panel i grafikoni Acrel ADW300 električnog mjerila (#99)
    pmGroupLine: "Vod",
    pmGroupPower: "Snaga",
    pmGroupEnergy: "Ukupna energija",
    pmVoltageA: "Napon L1",
    pmVoltageB: "Napon L2",
    pmVoltageC: "Napon L3",
    pmVoltageAB: "Napon L1-L2",
    pmVoltageBC: "Napon L2-L3",
    pmVoltageCA: "Napon L3-L1",
    pmCurrentA: "Struja L1",
    pmCurrentB: "Struja L2",
    pmCurrentC: "Struja L3",
    pmActivePower: "Aktivna snaga",
    pmActivePowerA: "Aktivna snaga L1",
    pmActivePowerB: "Aktivna snaga L2",
    pmActivePowerC: "Aktivna snaga L3",
    pmReactivePower: "Reaktivna snaga",
    pmReactivePowerA: "Reaktivna snaga L1",
    pmApparentPower: "Prividna snaga",
    pmApparentPowerA: "Prividna snaga L1",
    pmPowerFactor: "Faktor snage",
    pmPowerFactorA: "Faktor snage L1",
    pmEnergyTotal: "Ukupna energija",
    pmEnergyForward: "Preuzeta energija",
    pmEnergyReverse: "Predata energija",
    pmReactiveEnergyForward: "Preuzeta reaktivna energija",
    pmReactiveEnergyReverse: "Predata reaktivna energija",
    pmPtRatio: "PT odnos",
    pmCtRatio: "CT odnos",
    pmSinglePhase: "Jednofazno",
    pmPhaseCount: "{n} faze",
    pmCumulative: "kumulativno",
    pmHistoryTitle: "Istorija električnih veličina",
    pmNoParameterSelected: "Izaberite parametar za prikaz",
    pmNegativePowerNote: "Aktivna snaga je negativna. To znači da snaga teče u smjeru koji mjerilo naziva obrnutim — vod predaje energiju ili je CT klešta postavljena naopako. Koje od toga važi još nije potvrđeno na mjerilu, pa se znak prikazuje tačno kako je izmjeren.",
    pmCtRatioOneNote: "CT odnos 1 znači da se ne primjenjuje skaliranje kliješta; provjerite da odgovara ugrađenim kliještima",
    pmUnverifiedNote: "Mjerilo emituje i kanale čije značenje ili skaliranje još nije potvrđeno. Čuvaju se, ali se ne prikazuju kao mjerenja:",
    pmChartUnverifiedNote: "Prikazuju se samo kanali čije je skaliranje potvrđeno u odnosu na očitavanja samog mjerila. Nepovezana faza se izostavlja, a ne crta na nuli, a prekid linije znači izostao uplink, ne očitavanje nule.",
    normalStatus: "Normalno",
    alertStatus: "upozorenja",
    thresholdExceeded: "Prag prekoračen",
    enterBothDates: "Molimo izaberite oba datuma.",
    // Od #184 samo ograničenje izvještaja o koridoru; grafikoni više nemaju
    // maksimalni opseg.
    maxRange: "Maksimalni opseg je 6 mjeseci.",
    endAfterStart: "Krajnji datum mora biti nakon početnog.",
    customRangePrompt: "Izaberite početni i krajnji datum, zatim pritisnite Primijeni.",

    // Donja granica opsega grafikona (#184).
    minRange: "Najraniji dostupni datum je 1. januar 2026. Sistem nema mjerenja prije tog datuma.",
    preDataPartial: "Ovaj uređaj nema očitavanja prije {date}. Prazan period prije tog datuma znači da uređaj tada još nije bio u radu, a ne da je došlo do prekida.",
    preDataNone: "Ovaj uređaj nema očitavanja prije {date}, pa u ovom opsegu nema ničega za prikaz. Nije riječ o prekidu.",

    // Objelodanjivanje agregacije grafikona (#108)
    aggPlotRaw: "Sirovo",
    aggPlotAggregated: "Agregirano",
    aggNotice: "Agregirano: svaka točka obuhvata ~{span} i predstavlja srednju vrijednost {n} očitavanja. Osjenčeni pojas je najniže i najviše stvarno očitavanje u tom intervalu, a linija je njihova srednja vrijednost.",
    aggNoticeNoSpan: "Agregirano: svaka točka predstavlja srednju vrijednost {n} očitavanja. Osjenčeni pojas je najniže i najviše stvarno očitavanje u tom intervalu, a linija je njihova srednja vrijednost.",
    aggNoticeLatest: "Najnovija točka je posljednje pojedinačno očitavanje, pa se poklapa sa panelom u realnom vremenu.",
    aggRawNotice: "Prikazano kako je primljeno — {rows} očitavanja, bez agregacije.",
    aggRawCapped: "Sirovi prikaz je ograničen na {cap} očitavanja, a ovaj opseg ih ima {rows}, pa se prikazuje agregirani niz.",
    aggIntervalTooltip: "{n} očitavanja, {start} – {end}",
    aggRangeTooltip: "najniže {min}, najviše {max}",
    aggLatestTooltip: "posljednje očitavanje",

    // Trend degradacije (#130). Svaka vrijednost se prikazuje uz prozor u kojem
    // je izmjerena, i ništa ovdje ne smije podrazumijevati datum otkaza.
    dtTitle: "Trend degradacije",
    dtLoading: "Učitavanje trenda…",
    dtError: "Trend nije moguće učitati",
    dtVelocity: "Brzina odstupanja",
    dtAcceleration: "Promjena brzine",
    dtAdjusted: "Bez uticaja temperature",
    dtDegPerDay: "°/dan",
    dtOverWindow: "kroz {days} dana",
    dtWindowDates: "{start} – {end}",
    dtVsPrevious: "u odnosu na prethodnih {days} dana, {start} – {end}",
    dtTempCoefficient: "{coef} °/°C, r = {r}, {min}–{max} °C u ovom prozoru",
    dtTempNotAdjusted: "Temperatura nije uklonjena",
    dtTempSource: "Izvor temperature",
    dtTempSourceWindow: "{sources}, za period {start} – {end}.",
    dtTempSourceCrosses: "Ovaj prozor prelazi granicu izvora na {at}: miješa ERA5 reanalizu sa ćelije mreže udaljene 3,2 km i Open-Meteo procjenu za lokaciju. Dva izvora nikada nisu radila uporedo, pa razlika među njima nikada nije izmjerena i sada se ne može utvrditi. Gornje r obuhvata oba i nije jedno mjerenje.",
    dtTempSourceSingle: "Jedan izvor kroz cijeli prozor, pa gornje r ne obuhvata promjenu izvora.",
    dtSourceEra5: "ERA5 reanaliza, 3,2 km dalje",
    dtSourceLive: "Open-Meteo procjena za tačku",
    dtSourceUnlabelled: "redovi bez zabilježenog izvora",
    dtNotMeasurable: "Nije mjerljivo",
    dtAxes: "Po osama",
    dtFlag: "Pokazatelj trenda",
    dtFlagRaised: "Aktiviran",
    dtFlagNotRaised: "Nije aktiviran",
    dtFlagUnassessed: "Nije ocijenjeno",
    dtFlagUncalibrated: "nekalibrisan prag, namjerno prikazan bez boje: nijedan stub ovdje još nije degradirao, pa nema primjera po kojem bi se prag postavio",
    dtFitNote: "Prilagođeno na {bins} od {total} intervala po {span} h, iz {readings} očitavanja. {excluded} uplinkova nije imalo mjerenje ugla i odbačeni su umjesto da budu upisani kao nula.",
    dtDisclaimer: "Trend, ne predviđanje. Pokazuje koliko se brzo odstupanje mijenja i da li se ta brzina mijenja. Nije prognoza otkaza i ne sadrži datum. Poredi se samo sa prošlošću ovog stuba, nikada sa drugim stubom — montaža, izloženost i položaj senzora se razlikuju. Izmjereni nagib se mijenja i sa temperaturom, zbog čega postoji treća vrijednost.",
    dtReasonTooFewReadings: "manje od 50 očitavanja u ovom prozoru",
    dtReasonSparseCoverage: "manje od pola vremenskih intervala prozora ima očitavanje",
    dtReasonNoFit: "očitavanja ne podržavaju nagib",
    dtReasonNoVelocity: "nema brzine za ovaj prozor",
    dtReasonNoPreviousWindow: "prethodni prozor nema brzinu za poređenje",
    dtReasonInsufficientHistory: "manje od tri ranija prozora za poređenje",
    dtReasonDegenerateSpread: "raniji prozori ovog uređaja nemaju raspon za poređenje",
    dtReasonTooFewPairedBins: "previše malo ovog prozora ima odgovarajuće mjerenje temperature",
    dtReasonUnknown: "razlog nije naveden",

    // Authentication tiers
    login: "Prijava",
    logout: "Odjava",
    loginArrow: "Prijava →",
    // Account menu behind the mobile avatar
    accountMenu: "Nalog",
    accountNotSignedIn: "Niste prijavljeni",
    tierGuest: "Gost",
    tierUser: "Korisnik",
    tierSuperuser: "Super korisnik",
    lockedDriftTitle: "Prijavite se da vidite analizu nagiba",
    lockedAnalysisTitle: "Prijavite se da vidite detaljnu analizu",
    lockedAnomalies: "Prijavite se da vidite sve anomalije",
    simulatedBannerGuest: "⚠ Simulirani podaci — prijavite se za punu analitiku",
    simulatedBannerUser: "⚠ Simulirani podaci — kontaktirajte administratora za super korisnički pristup",

    // Satelitski snimak po čvoru (#109). Kao i u engleskoj verziji: datum,
    // oblačnost i rezolucija se nikada ne izostavljaju — bez njih se snimak od
    // 10 m/piksel ne razlikuje od podloge snimljene 2020-02-17 (rezolucija 0,5 m).
    // Starost podloge (#82). Izmjereno 2026-09-06: snimljeno 2020-02-17,
    // rezolucija 0,5 m. Vrijednost je upisana rucno i vremenom ce zastarjeti.
    basemapVintage: "Podloga ispod: snimljeno 2020-02-17, rezolucija 0,5 m. Nije aktuelno \u2014 provjerite prije upotrebe.",
    satSnapshotTitle: "Satelitski snimak",
    satSnapshotHint: "Najnoviji upotrebljiv Sentinel-2 snimak područja ~5 km oko ovog čvora.",
    satSnapshotFetch: "Učitaj snimak",
    satSnapshotRefresh: "Osvježi",
    satSnapshotLoading: "Učitavanje…",
    satSnapshotError: "Snimak nije mogao biti učitan",
    satSnapshotCaptured: "Snimljeno",
    satSnapshotToday: "danas",
    satSnapshotDaysAgo: "prije {n} dana",
    satSnapshotCloud: "Oblačnost",
    satSnapshotCloudScope: "(cijela scena)",
    satSnapshotResolution: "Rezolucija",
    satSnapshotPixel: "piksel",
    satSnapshotResolutionNote: "stub je manji od jednog piksela",
    satSnapshotSource: "Izvor",
    satSnapshotUnknown: "nije prijavljeno",
    satSnapshotAlt: "Sentinel-2 snimak u prirodnim bojama, snimljen {date}, scena {id}",
    satSnapshotOlder:
      "Starija scena — {n} novijih prolaza bilo je previše oblačno.",
    satSnapshotNoScene: "Nema upotrebljivog snimka",
    satSnapshotNoSceneBody:
      "Svi Sentinel-2 prolazi u posljednjih {days} dana imali su više od {pct}% oblačnosti.",
    satSnapshotUnavailable: "Status snimka nepoznat",
    satSnapshotUnavailableBody: "Copernicus katalog nije dostupan.",
    satSnapshotNoImage: "Scena pronađena, snimak nije dostupan",
    satSnapshotNoImageBody:
      "Katalog navodi ovu scenu, ali snimak nije mogao biti generisan.",
    satSnapshotCooldown:
      "Osvježeno maloprije — novi snimak može se zatražiti za {n} s.",

    // Nadzor izvještavanja senzora (#155).
    //
    // Tri različita kvara, tri različite poruke. "Van mreže" bi ih sve spojilo
    // u jednu riječ, a upravo je razlika među njima ono što je korisno: uređaj
    // koji je zaćutao traži bateriju ili gateway, uređaj koji nikada nije
    // javio traži da se dovrši registracija, a uređaj koji šalje prazne okvire
    // ne traži ni jedno ni drugo — veza mu radi.
    watchdog: "Nadzor izvještavanja",
    watchdogHint:
      "Da li svaki uređaj još uvijek izvještava, mjereno prema vlastitoj učestalosti tog uređaja, a ne jednim pragom za cijelu mrežu.",
    watchdogLoading: "Provjera uređaja…",
    watchdogError: "Nadzor nije dostupan",
    watchdogErrorHint:
      "Ovo znači da provjera nije obavljena, a ne da je sve u redu. Iz toga ne slijedi ništa o uređajima.",
    watchdogAllReporting: "Svi uređaji izvještavaju ({n})",
    watchdogFindings: "{n} od {total} uređaja zahtijeva pažnju",
    watchdogStateReporting: "Izvještava",
    watchdogStateSilent: "Prestao da izvještava",
    watchdogStateNeverReported: "Nikada nije javio",
    watchdogStateNotMeasuring: "Izvještava, ali ne mjeri",
    watchdogStateUnknown: "Nije moguće provjeriti",
    watchdogSilentBody: "Posljednje očitanje {ago}. Vlastiti prag ovog uređaja je {threshold}.",
    watchdogNeverReportedBody:
      "Registrovan, bez ijednog očitanja u bilo kom trenutku. Registracija nikada nije dovršena i ovo se neće riješiti samo od sebe.",
    watchdogNotMeasuringBody:
      "{empty} od posljednjih {sampled} poruka nije nosilo mjerenje. Veza radi, pa ovo ne može vidjeti ništa što prati tišinu.",
    watchdogUnknownBody:
      "Očitanja ovog uređaja nije bilo moguće pročitati, pa je stanje nepoznato. To ne znači da je uređaj ispravan.",
    watchdogReportingBody: "Posljednje očitanje {ago}. Prag {threshold}.",
    watchdogEmptyNote: "{empty} od posljednjih {sampled} poruka nije nosilo mjerenje.",
    watchdogSince: "Od {time}",
    watchdogThresholdFromCadence: "izvedeno iz vlastite učestalosti ovog uređaja",
    watchdogThresholdFromFleet: "učestalost nije mjerljiva — koristi se mrežna postavka",
    watchdogListIncomplete: "Ova lista može biti nepotpuna",
    watchdogListIncompleteHint:
      "Jedan registar uređaja nije mogao biti pročitan, pa neki uređaj može nedostajati na listi umjesto da bude prikazan kao ispravan.",
    watchdogScopeNote:
      "Uređaj čija učestalost izvještavanja opada, ali još nije pala na nulu, i dalje se prikazuje kao da izvještava. Otkrivanje opadajuće učestalosti nije implementirano.",

    // ── Pomoć na ekranu (#171) ──────────────────────────────────────────────
    //
    // Ista pravila kao u engleskoj verziji, i iz istog razloga: nijedan niz ne
    // ponavlja svoju oznaku, jedna tvrdnja postoji samo na jednom mjestu
    // (helpNdviClaim), napomena o simuliranim podacima ide prva, a datumi i
    // brojevi su nosivi podaci a ne ukras.
    //
    // I ovdje važi pravilo kratkih rečenica. Prva verzija ovog bloka bila je
    // tačna i nečitljiva — pasusi od sedamdeset riječi u kojima je ograda
    // sakrivena u posljednjoj zavisnoj rečenici. Zato je podjela na `helpX`
    // (šta broj jeste) i `helpXNot` (šta nije): dva kratka pasusa, a drugi je
    // uvijek granica prvog.
    //
    // Ovo NIJE prevod reda po red — to su ograde, i labav prevod „nije mjerenje
    // sigurnosnog rastojanja“ znači da proizvod tvrdi nešto drugo polovini svoje
    // publike. Rečenice su pisane na crnogorskom (ijekavica, latinica).
    helpAria: "Pomoć: {label}",
    helpSimulatedLead:
      "⚠ Ovi brojevi nisu stvarni. Generisani su za demonstraciju. Nijedan senzor ih nije izmjerio i ne govore ništa o stvarnom dalekovodu.",
    helpLabelBaseline: "Osnovna vrijednost",
    helpLabelDeviceStatus: "Status uređaja",
    helpLabelCorridorNdvi: "NDVI koridora",

    helpUnknownValue:
      "Upitnik znači da nemamo odgovor: provjera nije obavljena ili nije uspjela. To nije nula i nije potvrda da je sve u redu.",

    helpFiresCount:
      "Koliko je toplih tačaka NASA FIRMS (sateliti VIIRS i MODIS) našao unutar nadgledanog područja u posljednjih {hours} h. Topla tačka je jedan piksel koji je bio mnogo topliji od tla oko njega.",
    helpFiresCountNot:
      "Topla tačka nije potvrđeni požar. Fabrike, mašine i odsjaj sunca na vodi izgledaju isto iz orbite. Sateliti prelaze samo nekoliko puta dnevno, pa mali broj ne znači da neko posmatra.",

    helpWatchdogCoverage:
      "Da li svaki uređaj još uvijek šalje podatke. Svaki se mjeri prema vlastitom uobičajenom ritmu, izvedenom iz njegovih ranijih poruka. Uređaji izvještavaju vrlo različitom brzinom, pa bi jedno pravilo za cijelu mrežu bilo pogrešno.",
    helpWatchdogCoverageNot:
      "Neki uređaji šalju u naletima, pa pošalju više nego što procjena očekuje. Njihova raspoloživost u izvještaju o koridoru tada može preći 100%. To znači da je procjena bila niska, a ne da je nešto u kvaru — u tom slučaju čitajte „pokrivenost“.",

    helpNdviClaim:
      "NDVI mjeri koliko je tlo zeleno unutar koridora. Ne mjeri koliko su biljke visoke, koliko su blizu vodova, niti koliko su opasne.",
    helpCorridorNdvi:
      "Ova vrijednost dolazi sa satelita Sentinel-2, rezolucije 10 m po pikselu, kao prosjek za cijeli koridor — oko 180 km². Uvijek pročitajte datum pored nje: provjera se radi jednom sedmično, a oblaci mogu ostaviti stariji snimak na snazi.",
    helpCorridorNdviNot:
      "Jedan prosjek za veliko područje ne govori ništa o pojedinačnoj dionici između dva stuba. A ako vrijednost padne, uzrok nije poznat: požar, sječa, suša, žetva i ispaša izgledaju isto iz svemira.",
    helpSegmentNdvi:
      "Isto mjerenje, ali izvedeno posebno za svaku dionicu između dva stuba, pa se promjena može locirati. Uz svaku dionicu stoji starost njene vrijednosti. Dionica bez vrijednosti navodi razlog: oblačnost, izvan snimka ili neuspjela provjera.",

    helpDriftValue:
      "Koliko se ovaj stub pomjerio od vlastitog početnog položaja, u stepenima, iz uglova X, Y i Z senzora nagiba. „Trenutni pomak“ je najnovije očitavanje u opsegu. „Vršni pomak“ je najveće u njemu.",
    helpDriftValueNot:
      "To nije apsolutni ugao stuba i nije naginjanje u jednom smjeru: stub koji jače osciluje na vjetru takođe podiže ovaj broj. Poredite ga samo sa prošlošću ovog stuba — montaža i izloženost razlikuju se od stuba do stuba.",
    helpDriftBaseline:
      "Početni položaj ovog uređaja: prosjek njegovih prvih 14 dana očitavanja, po jedna vrijednost za svaku osu, u stepenima. Utvrđuje se pri ugradnji i nikada se ne računa ponovo. Svaka vrijednost pomaka na ovom grafikonu mjeri se od nje.",
    helpDriftBaselineNot:
      "To nije ispravan ni vertikalan položaj. Ako je senzor ugrađen nakrivo, osnovna vrijednost to jednostavno bilježi kao normalno. Očitavanja koja su okinula hardverski prag, kao i ona bez ugla, izostavljena su — datumi i brojevi ispisani su ispod vrijednosti.",

    helpMlAnomaly:
      "Na ovu listu pišu dva detektora. Prvi traži očitavanja koja su sama po sebi ekstremna. Drugi uči šta je normalno za svaki stub iz njegovih posljednjih 30 dana. Koristi Isolation Forest, koji se obučava svake noći, i označava očitavanja koja su neobična za taj stub.",
    helpMlAnomalyNot:
      "Red sa oznakom „medium“ uvijek dolazi od drugog detektora i namjerno je tih: bez e-pošte, bez alarma. Ocjenjuje samo očitavanja koja nisu već okinula prvi detektor, pa nikada ponovo ne prosuđuje ono što je hardver označio. „low“ su svi događaji pokreta i ostaju skriveni dok ih ne zatražite.",
    helpAnomalyConfidence:
      "Vlastita ocjena detektora za ovu jednu detekciju, od 0 do 1, ovdje prikazana u procentima.",
    helpAnomalyConfidenceNot:
      "To nije vjerovatnoća. 80% ne znači da su četiri od pet detekcija bile stvarne — ove ocjene nikada nisu provjerene prema onome što se zaista dogodilo. Većina ih se nalazi pri vrhu skale, pa detekcije razdvajaju samo grubo. Koristite ih za poređenje detekcija istog detektora, i ni za šta drugo.",

    helpTemperatureStats:
      "Temperatura vazduha za isti period kao i trend, u °C. Usrednjava se po satu, a ne po poruci. Uređaji su mijenjali učestalost izvještavanja, pa bi običan prosjek dao prevelik značaj periodu sa više poruka. API objavljuje tu razliku kao cadence_bias_c.",
    helpTemperatureStatsNot:
      "Nije riječ o jednom izvoru. Do 28. jula 2026. temperatura je dolazila iz modela ERA5, sa ćelije mreže udaljene 3,2 km. Poslije tog datuma dolazi iz Open-Meteo procjene za samu lokaciju. Dva izvora nikada nisu radila istovremeno, pa razlika među njima nikada nije izmjerena — period koji prelazi taj datum miješa oba.",

    helpSatelliteSnapshot:
      "Snimak sa satelita Sentinel-2, rezolucije 10 m po pikselu, za područje od oko 5 km oko ovog čvora. To je najnoviji prolaz koji je bio dovoljno vedar, pa datum pripada snimku, a ne današnjem danu. Oblačni prolazi se preskaču, zbog čega upotrebljiv snimak može biti star sedmicama.",
    helpSatelliteSnapshotNot:
      "Stub je ovdje manji od jednog piksela. Vidi se tlo oko čvora, ali ne i sama konstrukcija.",

    helpDeviceStatus:
      "Najgore stanje među svim senzorima u ovom čvoru. Zeleno: izvještava normalno. Narandžasto: baterija slaba. Sivo: ništa nije primljeno duže od granice za stanje van mreže, koja se podešava u Podešavanjima. Crveno: upozorenje.",
    helpStatusUnknown:
      "Blijedo sivo — na mapi šupalj prsten — znači nešto drugo: nijedan zapis o stanju nije odgovorio za ovaj uređaj, pa nismo mogli utvrditi stanje. Nikada ne znači „ispravan“ i nikada ne znači „van mreže“. Ako jedan senzor čvora nedostaje u odgovoru, čvor prikazuje ovo i imenuje taj senzor.",
    // Opteretivost voda na čvoru meteorološke stanice (#247).
    dlrTabWeather: "Vrijeme",
    dlrTab: "Opteretivost voda (DLR)",
    betaBadge: "Beta",
    betaBadgeWhy: "Beta: samo okvirno. Ova vrijednost opisuje jedan raspon, a ne cijeli vod, i nikada nije provjerena u odnosu na izmjerenu temperaturu provodnika. Ne koristiti kao osnovu za upravljanje opterećenjem.",
    dlrTitle: "Opteretivost na ovom rasponu (okvirno)",
    dlrSubtitle: "Dinamička opteretivost voda izračunata iz vremena izmjerenog na ovoj stanici. Odnosi se samo na ovaj raspon, ne na cijeli vod, i nije vrijednost po kojoj se upravlja opterećenjem.",
    dlrSimulated: "Primjer podataka. Podešavanja i opteretivost ispod pokazuju kako panel radi i ne opisuju nijedan stvarni vod.",
    dlrLoadError: "Opteretivost voda nije moguće učitati",
    dlrNotConfigured: "Još nema opteretivosti. Unesite podešavanja voda ispod: provodnik, najveću temperaturu provodnika, statičku opteretivost, pravac raspona i nadmorsku visinu.",
    dlrInvalidSettings: "Sačuvana podešavanja voda više nisu ispravna, pa se opteretivost ne prikazuje. Provjerite ih ispod i ponovo sačuvajte.",
    dlrUnknownStale: "Nema opteretivosti: posljednje potpuno očitavanje stanice staro je {age} min, a granica je {max} min. Opteretivost iz starog vremena opisivala bi neki drugi sat.",
    dlrUnknownNoReading: "Nema opteretivosti: stanica u posljednjih 6 sati nije poslala očitavanje sa temperaturom vazduha i brzinom vjetra zajedno.",
    dlrUnknownNoPosition: "Nema opteretivosti: ovaj čvor nema položaj, a od njega zavisi ugao sunca.",
    dlrUnknownMissingField: "Nema opteretivosti: posljednjem očitavanju nedostaje vrijednost potrebna za proračun.",
    dlrUnknownOther: "Opteretivost nije bilo moguće izračunati.",
    dlrSettingsTitle: "Podešavanja voda",
    dlrEdit: "Izmijeni",
    dlrView: "Prikaži",
    dlrFooter: "Samo okvirno. Izračunato za ovaj raspon, sa podešavanjima iznad i vjetrom izmjerenim na visini stanice. Nije provjereno prema izmjerenoj temperaturi provodnika.",
    dlrRatingAt: "Opteretivost na {t} °C",
    dlrStatic: "Statička opteretivost",
    dlrRatio: "U odnosu na statičku",
    dlrSunAlone: "Opteretivost 0 A: po ovako mirnom vremenu samo sunce zagrijava provodnik na oko {temp} °C, iznad granice od {tmax} °C, prije nego što poteče ikakva struja.",
    dlrBelowStatic: "Ispod statičke opteretivosti. To je uobičajeno po mirnom vremenu: statička opteretivost obično pretpostavlja stalan povjetarac, a sada je hlađenje slabije od toga.",
    dlrWeatherUsed: "Korišćeno vrijeme",
    dlrWindAtStation: "Vjetar na stanici ({h} m)",
    dlrAttack: "Ugao vjetra prema provodniku",
    dlrAttackMeasured: "{deg}°, izmjereno",
    dlrAttackAssumed: "{deg}°, pretpostavljeno: ispod {v} m/s pravac vjetra nije pouzdan",
    dlrReadingAge: "Očitavanje",
    dlrMinutesAgo: "prije {n} min",
    dlrHeatBalance: "{method}. Na najvećoj temperaturi, po metru provodnika: konvekcija hladi {conv} W, zračenje hladi {rad} W, sunce grije {sun} W.",
    dlrHeightCorrected: "Uz korekciju visine za teren tipa „{terrain}“ (vjetar × {factor}, {wind} m/s na provodniku), opteretivost bi bila {amps} A. To je pretpostavka o profilu vjetra, a ne opteretivost iznad.",
    dlrLoadTitle: "Trenutno opterećenje (nije obavezno)",
    dlrLoadByCurrent: "Struja",
    dlrLoadByPower: "P, Q i napon",
    dlrLoadCurrent: "Struja voda",
    dlrLoadVoltage: "Linijski napon",
    dlrLoad: "Opterećenje",
    dlrUtilisation: "Od opteretivosti",
    dlrHeadroom: "Rezerva",
    dlrConductorTemp: "Provodnik, procjena",
    dlrCompareRating: "Opteretivost",
    dlrRowStatic: "Statička opteretivost",
    dlrRowAar: "Prilagođena temperaturi",
    dlrRowDlr: "Dinamička, sada",
    dlrGainOverStatic: "Vrijeme izmjereno na ovom rasponu daje {amps} A veću rezervu nego statička opteretivost u ovom trenutku.",
    dlrLoadBelowStatic: "Vrijeme izmjereno na ovom rasponu spušta opteretivost {amps} A ispod statičke u ovom trenutku. Statička opteretivost pretpostavlja povoljnije uslove od ovih.",
    dlrAarNote: "Prilagođena temperaturi koristi izmjerenu temperaturu vazduha uz brzinu vjetra od {wind} m/s koju pretpostavlja statička opteretivost. Korak od nje do dinamičke vrijednosti je ono što donosi mjerenje vjetra ovdje; korak od statičke do nje već je postojao u vašoj opteretivosti.",
    dlrLoadPrivate: "Ono što ovdje unesete ostaje u ovom pregledaču. Ne šalje se serveru i ne čuva se.",
    dlrSetConductor: "Provodnik",
    dlrConductorCustom: "Drugi provodnik",
    dlrCustomDiameter: "Spoljni prečnik",
    dlrCustomStrand: "Prečnik spoljne žice",
    dlrCustomRLow: "AC otpor na nižoj temperaturi",
    dlrCustomTLow: "Niža temperatura",
    dlrCustomRHigh: "AC otpor na višoj temperaturi",
    dlrCustomTHigh: "Viša temperatura",
    dlrSetTmax: "Najveća temperatura provodnika",
    dlrSetStatic: "Statička opteretivost",
    dlrSetAzimuth: "Pravac raspona od sjevera",
    dlrSetElevation: "Nadmorska visina",
    dlrSetEmissivity: "Emisivnost",
    dlrSetAbsorptivity: "Apsorptivnost sunčevog zračenja",
    dlrSetStationHeight: "Visina stanice",
    dlrSetConductorHeight: "Visina provodnika",
    dlrSetStaticWind: "Vjetar koji pretpostavlja statička opteretivost",
    dlrAzimuthPrefill: "Najbliži ucrtani vod ovdje ide pod {deg}°. Provjerite prema rasponu: na stubu gdje vod skreće, dva raspona se razlikuju.",
    dlrAzimuthUse: "Koristi ovaj pravac",
    dlrAzimuthOffCorridor: "Ovaj čvor je označen kao da nije na praćenom vodu, pa najbliži ucrtani vod možda nije onaj kod ove stanice.",
    dlrSetMethod: "Metoda proračuna",
    dlrSetHeightCorrection: "Korekcija vjetra po visini",
    dlrTerrain_off: "Isključena (vjetar kako je izmjeren)",
    dlrTerrain_open: "Otvoren teren",
    dlrTerrain_scattered: "Rijetke prepreke",
    dlrTerrain_rough: "Neravan teren",
    dlrLastChanged: "Posljednja izmjena: {who}, {when}, verzija {version}",
    dlrSaveConflict: "Neko drugi je u međuvremenu izmijenio ova podešavanja. Ponovo učitajte panel i pokušajte opet.",
    dlrSaving: "Čuvanje…",
    dlrSave: "Sačuvaj podešavanja",
    dlrReadOnly: "Samo superkorisnik može mijenjati ova podešavanja.",
    dlrErrRange: "Mora biti između {min} i {max}",
    dlrErrRequired: "Obavezno",
    dlrErrNumber: "Unesite broj",
    dlrErrUnknown: "Nepoznata vrijednost",
    dlrErrStrand: "Žica mora biti manja od polovine provodnika",
    dlrErrNotAboveLow: "Mora biti veće od niže vrijednosti",
  },
};

export default translations;
